import React, { useEffect, useMemo, useRef, useState } from "react";
import Dialog from "@mui/material/Dialog";
import {
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Divider,
    FormControl,
    FormControlLabel,
    IconButton,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
    Skeleton
} from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import CancelIcon from "@mui/icons-material/Cancel";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import WarningIcon from "@mui/icons-material/Warning";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useForm } from "react-hook-form";
import { useModalStyles } from "../../common/useModalStyles";
import CustomDropdownField from "../../common/CustomDropdownFiled";
import CustomTextField from "../../common/CustomTextField";
import CustomDatePicker from "../../common/CustomDatePicker";
import { getPracticesByUserRoleUserSlice, useUserReducer } from "../../../redux/slice/userSlice";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import { autoAddress, fetchAddressDetails, fetchPharmaciesList, setSingleAlertObj, showHideLoadingDialog, useCommonReducer } from "../../../redux/slice/commonSlice";
import CustomPatternTextField from "../../common/CustomPatternTextField";
import CustomAutocompleteField from "../../common/CustomAutocompleteField";
import { CodeTable, getCodeTableValuesByTableName, useCodetablesSlice } from "../../../redux/slice/codetableSlice";
import CustomRadioButton from "../../common/CustomRadioButton";
import { getPatientListByPracticeId, getPatientWithActivePatientInfo, usePatientReducer } from "../../../redux/slice/patient/patientSlice";
import {
    fullName,
    resolvePharmacyKey,
    pharmacyBillToRadioOptions,
    pharmacyBillToRxDefault,
    priceFormatter,
} from "../../../utils/Utility";
import { fetchActivePracticeProvidersData, usePracticeProvidersSlice } from "../../../redux/slice/practice/practiceProvidersSlice";
import { fetchActivePracticeLocationsData, usePracticeLocationsSlice } from "../../../redux/slice/practice/practiceLocationSlice";
import { fetchPracticeProductsDataUsingDateOfServiceAndProvider } from "../../../redux/slice/practice/practiceProductSlice";
import { fetchAvailableDrugDosabeByProduct, ProductDosageInfo, useProductListReducer } from "../../../redux/slice/product/productListSlice";
import { MarketProduct } from "../../../redux/slice/exploreMarket/exploreMarketSlice";
import {
    sendNotificationEmail,
    logRxGatingBlockedAttempt,
    logPrescriberConfirmation,
    PrescriberConfirmation,
    PrescriptionSubmitRequest,
    RxGatingBlockedAttemptRequest,
    LogPrescriberConfirmationRequest,
} from "../../../redux/slice/prescription/prescriptionSlice";
import moment from "moment";
import { getRxAgreementPracticeLookupService } from "../../../service/PrescriptionService";
import { fetchOrderDocumentsByIds } from "../../../redux/slice/order/OrderSlice";
import PDFViewer from "../../common/pdf/PDFViewer";
import { ClipboardList } from "lucide-react";
import DownloadIcon from "@mui/icons-material/Download";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import GenesisBillingFormPopup from "./GenesisBillingFormPopup";
import { ReceiptOutlined } from "@mui/icons-material";
import GppGoodOutlinedIcon from "@mui/icons-material/GppGoodOutlined";
import {
    GatingFailure,
    GatingResult,
    isSterileCompoundProduct,
    resolvePatientState,
    runPharmacyShipToGate,
    RxOrderGatingInput,
} from "../../../utils/rxOrderGating";
import {
    fetchPharmacyLicenses,
    usePharmacyLicenseReducer,
} from "../../../redux/slice/pharmacy/pharmacyLicenseSlice";
import { PHARMACY_CONFIG_BILLING_CODE, PHARMACY_CONFIG_KEY_GENESIS, PRESCRIPTION_ACTIONS, PRODUCT_CONFIG_VALUE } from "../../../constants/applicationConstants";
import { SUCCESS_CODE } from "../../../service/ServiceConstants";

export interface QuantityOption {
    label: string;
    quantity: number;
}

interface SelectedDosage {
    quantity: number;
    refills: number;
    sig: string;
}

interface PrescriptionModalProps {
    product: MarketProduct;
    isOpen: boolean;
    /** Second arg is true when user closes after a successful submit (success screen Close / X). */
    onClose: (closedAfterSuccessfulSubmit?: boolean) => void;
    onSubmit?: (payload: any) => Promise<any[] | null>;
    initialPracticeId?: number;
    initialPatientId?: number;
    /** When true (practice chosen in Rx product popup), practice cannot be changed. */
    lockPracticeSelection?: boolean;
    /** Explore Market → Request Rx: show agreement PRICE on step 1 after practice is selected (Genesis only). CS-2075: lockPracticeSelection shows it too. */
    fromExploreMarket?: boolean;
    /** Product page → Record Rx: EXECUTED-agreement practice list + pricing (mandatory agreement or Genesis). */
    useExecutedAgreementPracticeRules?: boolean;
}

export type Option = {
    value: string,
    label: string
};

type Prescription = {
    practiceId: number;
    patientId: number;
    isNewPatient: boolean;
    newPatient: {
        firstName: string;
        lastName: string;
        birthDate: string;
        phone: string;
        gender: string;
        patientAddressInfo: {
            patientStreetAddress: string,
            patientUnitNumber: string,
            patientCity: string,
            patientState: string,
            patientZipCode: string,
        }
    },
    patientDetails: {
        firstName: string;
        middleName: string;
        lastName: string;
        birthDate: string;
        gender: string;
        phone: string;
    },
    providerDetails: {
        npi: string;
        dea: string;
        firstName: string;
        lastName: string;
    },
    providerId: number;
    practiceLocationId: number;
    billTo: string;
    shipTo: string;
    billToAddress: {
        address: string;
        city: string;
        state: string;
        zip: string;
        phone: string;
    };
    shipToAddress: {
        address: string;
        city: string;
        state: string;
        zip: string;
        phone: string;
    };
}

const ACCENT_LIGHT = "rgba(20,184,166,0.08)";   
const ACCENT_BORDER = "rgba(20,184,166,0.30)"; 
const SUCCESS = "#16a34a";
const SUCCESS_LIGHT = "rgba(22,163,74,0.10)";

const parseAgreementUnitExtendedPrice = (value: unknown): number | null => {
    if (value == null || value === "") return null;
    if (typeof value === "number" && Number.isFinite(value)) return value;
    const parsed = Number(String(value).replace(/[^0-9.-]/g, ""));
    return Number.isFinite(parsed) ? parsed : null;
};

/** CS-2075 — one price for an SKU list when every SKU shares it (SKU-priced agreements have no flat price). */
const singleSkuUnitExtendedPrice = (skus: any[] | null | undefined): number | null => {
    if (!Array.isArray(skus)) return null;
    const prices = Array.from(new Set(
        skus.map((sku) => parseAgreementUnitExtendedPrice(sku?.extendedPrice)).filter((price): price is number => price != null),
    ));
    return prices.length === 1 ? prices[0] : null;
};

// CS-2075
function computeScheduleLineTotal(
    unitExtendedPrice: number | null,
    quantity: number,
): number | null {
    if (unitExtendedPrice == null || !Number.isFinite(quantity) || quantity <= 0) return null;
    return Math.round(unitExtendedPrice * quantity * 100) / 100;
}

function formatScheduleLineTotalLabel(
    unitExtendedPrice: number | null,
    quantity: number,
): string | null {
    const lineTotal = computeScheduleLineTotal(unitExtendedPrice, quantity);
    return lineTotal != null ? priceFormatter(lineTotal) : null;
}

const TEN_DIGIT_PHONE_MESSAGE = "Enter a valid 10-digit phone number.";

function countPhoneDigits(value: string | undefined): number {
    return (value || "").replace(/\D/g, "").length;
}

function validateTenDigitPhoneIfPresent(value: string | undefined): true | string {
    const n = countPhoneDigits(value);
    if (n === 0) return true;
    if (n === 10) return true;
    return TEN_DIGIT_PHONE_MESSAGE;
}

function formatPracticeLocationLine(loc: {
    streetAddress?: string;
    unitNumber?: string;
    city?: string;
    state?: string;
    zipCode?: string;
} | null | undefined): string {
    if (!loc) return "";
    return [loc.streetAddress, loc.unitNumber, loc.city, loc.state, loc.zipCode]
        .filter((part) => part != null && String(part).trim() !== "")
        .join(", ");
}

const newPatientPhoneRules = {
    required: "Phone is required.",
    validate: validateTenDigitPhoneIfPresent,
};

const billShipPhoneRules = {
    required: "Phone number is required.",
    validate: validateTenDigitPhoneIfPresent,
};

const PrescriptionLoadingSkeleton = ({ tokens, pharmacyName }: { tokens: any; pharmacyName: string }) => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
        <Box sx={{ px: 3, py: 2.5, display: "flex", flexDirection: "column", gap: 2.5 }}>
            {/* Sub-header card skeleton with loading */}
            <Box sx={{
                border: `1px solid ${ACCENT_BORDER}`,
                borderRadius: "12px",
                backgroundColor: "rgba(20,184,166,0.04)",
                px: 2.5, py: 2,
                display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2,
            }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Box sx={{
                        width: 44, height: 44, borderRadius: "12px",
                        background: "linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        flexShrink: 0,
                    }}>
                        <CircularProgress size={22} sx={{ color: "#fff" }} />
                    </Box>
                    <Box>
                        <Typography sx={{ fontSize: "0.88rem", fontWeight: 700, color: tokens.textPrimary, lineHeight: 1.2 }}>
                            Submitting Rx Request...
                        </Typography>
                        <Typography sx={{ fontSize: "0.72rem", color: tokens.textSecondary, mt: 0.3 }}>
                            {`Submitting Rx Request to ${pharmacyName}...`}
                        </Typography>
                    </Box>
                </Box>
                <Skeleton variant="rounded" width={100} height={26} sx={{ borderRadius: "8px" }} />
            </Box>

            {/* Meta cards skeleton */}
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 1.5 }}>
                {[1, 2, 3, 4].map((i) => (
                    <Box key={`sk-meta-${i}`} sx={{ p: 1.5, border: `1px solid ${tokens.border}`, borderRadius: "10px" }}>
                        <Skeleton variant="rounded" width="50%" height={14} />
                        <Skeleton variant="rounded" width="80%" height={18} sx={{ mt: 0.5 }} />
                        {i === 1 && <Skeleton variant="rounded" width="90%" height={14} sx={{ mt: 0.3 }} />}
                    </Box>
                ))}
            </Box>

            {/* Success message + prescription card skeleton */}
            <Box sx={{
                border: `1px solid ${ACCENT_BORDER}`,
                borderRadius: "12px",
                backgroundColor: "rgba(20,184,166,0.03)",
                p: 2.5,
                display: "flex", flexDirection: "column", gap: 2,
            }}>
                <Skeleton variant="rounded" width="95%" height={18} />
                <Skeleton variant="rounded" width="70%" height={18} />
                <Box sx={{ p: 1.5, border: `1px solid ${tokens.border}`, borderRadius: "12px" }}>
                    <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                        <Skeleton variant="rounded" width={40} height={40} sx={{ borderRadius: "10px" }} />
                        <Box sx={{ flex: 1 }}>
                            <Skeleton variant="rounded" width="40%" height={18} />
                            <Skeleton variant="rounded" width="55%" height={14} sx={{ mt: 0.5 }} />
                        </Box>
                        <Skeleton variant="rounded" width={32} height={32} sx={{ borderRadius: "8px" }} />
                    </Box>
                </Box>
            </Box>

            {/* What happens next skeleton */}
            <Box sx={{ border: `1px solid ${tokens.border}`, borderRadius: "12px", p: 2.5 }}>
                <Skeleton variant="rounded" width={160} height={20} sx={{ mb: 1.5 }} />
                {[1, 2, 3, 4].map((i) => (
                    <Box key={`whn-sk-${i}`} sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 1 }}>
                        <Skeleton variant="circular" width={24} height={24} />
                        <Skeleton variant="rounded" width={`${60 + i * 5}%`} height={14} />
                    </Box>
                ))}
            </Box>

        </Box>
    </Box>
);

function resolveValidationFocusTarget(marker: HTMLElement): HTMLElement | null {
    const el = marker;

    const isFocusableControl = (node: HTMLElement | null): boolean => {
        if (!node) return false;
        if (node.hasAttribute("disabled") || node.getAttribute("aria-disabled") === "true") return false;
        const tag = node.tagName;
        if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || tag === "BUTTON") return true;
        if (node.getAttribute("role") === "combobox") return true;
        return false;
    };

    if (isFocusableControl(el)) return el;

    let found = el.querySelector<HTMLElement>(
        'input:not([type="hidden"]):not(:disabled), textarea:not(:disabled), select:not(:disabled), [role="combobox"]:not([aria-disabled="true"])'
    );
    if (found) return found;

    const formControl = el.closest(".MuiFormControl-root");
    if (formControl) {
        found = formControl.querySelector<HTMLElement>(
            'input:not([type="hidden"]):not(:disabled), textarea:not(:disabled), select:not(:disabled), [role="combobox"]'
        );
        if (found) return found;
    }

    const prevSibling = el.parentElement?.previousElementSibling;
    if (prevSibling) {
        found = prevSibling.querySelector<HTMLElement>(
            'input:not([type="hidden"]):not(:disabled), input[type="radio"]'
        );
        if (found) return found;
    }

    if (el.getAttribute("data-validation-error") === "true") {
        const drugStep = el.closest("[data-prescription-drug-step]");
        if (drugStep) {
            found = drugStep.querySelector<HTMLElement>(
                '[data-prescription-dosage-row] input[type="checkbox"]'
            );
            if (found) return found;
        }
    }

    return null;
}

function focusFirstValidationErrorInContainer(container: HTMLElement | null | undefined) {
    if (!container) return;
    const selector = [
        '[aria-invalid="true"]',
        '.MuiOutlinedInput-root.Mui-error input:not(:disabled)',
        '.MuiInputBase-root.Mui-error input:not(:disabled)',
        '[data-validation-error="true"]',
        "p.Mui-error",
        ".MuiFormHelperText-root.Mui-error",
    ].join(", ");

    const markers = Array.from(container.querySelectorAll<HTMLElement>(selector));
    for (const marker of markers) {
        const target = resolveValidationFocusTarget(marker);
        if (target) {
            target.scrollIntoView({ behavior: "smooth", block: "center" });
            target.focus({ preventScroll: true });
            return;
        }
    }
}

const PrescriptionModalPopup: React.FC<PrescriptionModalProps> = ({
    product,
    isOpen,
    onClose,
    onSubmit: onSubmitProp,
    initialPracticeId,
    initialPatientId,
    lockPracticeSelection = false,
    fromExploreMarket = false,
    useExecutedAgreementPracticeRules = false,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isDark = theme.palette.mode === "dark";
    const {
        tokens,
        paperSx,
        headerSx,
        iconBoxSx,
        bodySx,
        footerSx,
        primaryBtnSx,
        cancelBtnSx,
        dropdownWrapSx,
        inputFieldSx,
        datepickerWrapSx
    } = useModalStyles();

    const { practiceByUserRoleUserSlice, userObject, email } = useUserReducer();
    const { pharmacyInfoList } = useCommonReducer();
    const dispatch = useAppDispatch();
    const { pharmacyId, pharmacyName, pharmacyLicenses } = usePharmacyLicenseReducer();
    const gatePharmacyId = pharmacyId ?? pharmacyLicenses[0]?.pharmacy_id ?? 0;
    const gatePharmacyName =
        pharmacyName || pharmacyLicenses[0]?.pharmacy_name || "The pharmacy";
    const [loadingComplete, setLoadingComplete] = useState<Boolean>(false);
    const { practiceProvidersInfo } = usePracticeProvidersSlice();
    const { practiceLocationsInfo } = usePracticeLocationsSlice();
    const [step, setStep] = useState(0);
    const [submitted, setSubmitted] = useState(false);
    const steps = ["Practice & Patient", "Provider", "Bill & Ship", "Schedule", "Notice & Allergies"];
    const [isNewPatient, setIsNewPatient] = useState(false);
    const { productDosageList } = useProductListReducer();
    const [selectedDosages, setSelectedDosages] = useState<Record<number, SelectedDosage>>({});
    const [editingSigs, setEditingSigs] = useState<Record<number, boolean>>({});
    const [allergies, setAllergies] = useState("");
    /** Patient's current active medication — sent on patientDataRequest.patientActiveMedication. */
    const [medicationNote, setMedicationNote] = useState("");
    const { allCodeTablesValuesMap, allCodeTableValueFetchStatus } = useCodetablesSlice();
    const [stateList, setStateList] = useState<CodeTable[]>([]);
    const [loadingPatients, setLoadingPatients] = useState(false);
    const [loadingProviders, setLoadingProviders] = useState(false);
    const [loadingLocations, setLoadingLocations] = useState(false);
    const { patientList } = usePatientReducer();
    const [submitting, setSubmitting] = useState(false);
    const [pdfContent, setPdfContent] = useState<string>("");
    const [showPDF, setShowPDF] = useState<{ isShow: boolean; title: string; fileName: string }>({
        isShow: false,
        title: "",
        fileName: "",
    });
    const [previewingId, setPreviewingId] = useState<number | null>(null);
    const [selectedPatientDetails, setSelectedPatientDetails] = useState<any>(null);
    const [loadingPatientDetails, setLoadingPatientDetails] = useState(false);
    const [submittedPrescriptions, setSubmittedPrescriptions] = useState<any[]>([]);
    const [stepValidationAttempted, setStepValidationAttempted] = useState<Record<number, boolean>>({});
    const [showPharmacyBillingSetupPopup, setShowPharmacyBillingSetupPopup] = useState(false);
    const [showBillingInfoPopup, setShowBillingInfoPopup] = useState(false);
    /** CS-2075 — Genesis fills the account setup form in-app instead of downloading the PDF. */
    const [showGenesisBillingFormPopup, setShowGenesisBillingFormPopup] = useState(false);
    const [gatingFailures, setGatingFailures] = useState<GatingFailure[]>([]);
    const [showGatingBlockPopup, setShowGatingBlockPopup] = useState(false);
    const [showPrescriberConfirmPopup, setShowPrescriberConfirmPopup] = useState(false);
    const [prescriberConfirmChecked, setPrescriberConfirmChecked] = useState(false);
    const [prescriberConfirmation, setPrescriberConfirmation] = useState<PrescriberConfirmation | null>(null);
    /** Local draft prescription (BLOCKED Gate 1 and/or PENDING Gate 2); reused on audits and final submit. */
    const [blockedPrescriptionId, setBlockedPrescriptionId] = useState<number | null>(null);
    const [isLoggingPrescriberConfirmation, setIsLoggingPrescriberConfirmation] = useState(false);
    const prescriptionDialogPaperRef = useRef<HTMLDivElement | null>(null);
    /** Dedupes Gate 1 block audits so the same failure set is not logged on every re-render. */
    const lastLoggedGatingBlockKeyRef = useRef<string>("");
    /** Prevents re-checking the sole schedule row after the user manually unchecks it. */
    const scheduleDosageAutoSelectDoneRef = useRef(false);
    /** EXECUTED agreement unit price for selected practice (Explore Market + Rx list parity). */
    const [agreementUnitExtendedPrice, setAgreementUnitExtendedPrice] = useState<number | null>(null);
    /** CS-2075 — per-SKU prices from the same EXECUTED agreement row (Order screen parity) when the product carries none. */
    const [agreementSkuPrices, setAgreementSkuPrices] = useState<any[] | null>(null);
    /** EXECUTED-agreement practice lookup (Explore Market Genesis + Product Record Rx). */
    const [genesisAgreementLookup, setGenesisAgreementPractices] = useState<
        Array<{ practiceId: number; practiceName: string }>
    >([]);
    const [userPracticesLoaded, setUserPracticesLoaded] = useState(false);
    // CS-2075 — start in loading when the lookup will run, so the first paint isn't "No practice…" before the effect fires.
    const [genesisAgreementPracticesLoading, setGenesisAgreementPracticesLoading] = useState(() => {
        const isGenesis =
            resolvePharmacyKey(product?.manufacturerName) === PHARMACY_CONFIG_KEY_GENESIS;
        if (fromExploreMarket && isGenesis) return true;
        if (
            useExecutedAgreementPracticeRules &&
            (isGenesis ||
                product?.isAgreementRequired ||
                product?.agreementMode === PRODUCT_CONFIG_VALUE.MANDATORY)
        ) {
            return true;
        }
        return false;
    });

    const inputSx = (hasError = false) => ({
        "& .MuiOutlinedInput-root": {
            fontSize: "0.875rem",
            borderRadius: "8px",
            backgroundColor: tokens.inputBg,
            "& fieldset": { borderColor: hasError ? tokens.error : tokens.inputBorder },
            "&:hover fieldset": { borderColor: hasError ? tokens.error : tokens.accentHover },
            "&.Mui-focused fieldset": {
                borderColor: hasError ? tokens.error : tokens.accent,
                borderWidth: "1.5px",
            },
        },
        "& .MuiInputBase-input": { padding: "8px 12px", color: tokens.textPrimary },
        "& .MuiSelect-select": { padding: "8px 12px" },
    });

    const labelSx = {
        fontSize: "0.78rem",
        fontWeight: 600,
        color: tokens.textSecondary,
        mb: 0.6,
        display: "flex",
        alignItems: "center",
        gap: 0.3,
    };

    const {
        control,
        reset,
        setValue,
        getValues,
        trigger,
        clearErrors,
        resetField,
        watch,
        formState: { errors },
    } = useForm<Prescription>({ mode: "onSubmit", reValidateMode: "onChange" });

    const resetAll = () => {
        setStep(0);
        setSubmitted(false);
        setIsNewPatient(false);
        setSelectedDosages({});
        setEditingSigs({});
        setAllergies("");
        setMedicationNote("");
        setSelectedPatientDetails(null);
        setSubmittedPrescriptions([]);
        setSubmitting(false);
        setPdfContent("");
        setStepValidationAttempted({});
        setGatingFailures([]);
        setShowGatingBlockPopup(false);
        setShowPrescriberConfirmPopup(false);
        setPrescriberConfirmChecked(false);
        setPrescriberConfirmation(null);
        setBlockedPrescriptionId(null);
        lastLoggedGatingBlockKeyRef.current = "";
        scheduleDosageAutoSelectDoneRef.current = false;
        setAgreementUnitExtendedPrice(null);
        setAgreementSkuPrices(null); // CS-2075
        setGenesisAgreementPractices([]);
        setGenesisAgreementPracticesLoading(false);
        reset();
    };

    const handleClose = () => {
        onClose(submittedPrescriptions.length > 0);
        setTimeout(resetAll, 300);
    };

const watchedValues = watch();

    // Helper to get selected practice data
    const getSelectedPractice = useMemo(() => {
        const practiceId = getValues('practiceId');
        if (!practiceId || !practiceByUserRoleUserSlice?.length) return null;

        return practiceByUserRoleUserSlice.find(
            (p: any) => String(p.practiceId) === String(practiceId)
        );
    }, [watchedValues.practiceId, practiceByUserRoleUserSlice]);

    /**
     * Billing code this practice holds with the pharmacy that supplies the product.
     * Read from practice_pharmacy_config via the same lowercase-contains key as
     * the backend (genesis / sandsrx). It replaced the practice-level sandsRxARId column.
     */
    const practicePharmacyBillingCode = useMemo(() => {
        const configKey = resolvePharmacyKey(product?.manufacturerName);
        if (!configKey) return '';
        const code = getSelectedPractice?.practicePharmacyConfig?.[configKey]?.[PHARMACY_CONFIG_BILLING_CODE];
        return code == null ? '' : String(code).trim();
    }, [getSelectedPractice, product?.manufacturerName]);

    // Practice has a billing account with this pharmacy
    const isPracticeRegisteredWithPharmacy = practicePharmacyBillingCode !== '';

    /**
     * CS-2075 — Genesis card authorization form only needs a billing address, so it
     * starts from the practice's billing location (or the one picked for this Rx).
     */
    const genesisBillingPrefill = useMemo(() => {
        const practice: any = getSelectedPractice;
        const locations: any[] =
            (practiceLocationsInfo?.length
                ? practiceLocationsInfo
                : practice?.practiceLocations) || [];
        const selectedLocationId = Number(watchedValues?.practiceLocationId);
        const billingLoc = locations.find((l) => {
            const flag = l?.isBillingAddress;
            return flag === true || String(flag).toUpperCase() === "YES";
        });
        const loc =
            locations.find((l) => Number(l.locationId) === selectedLocationId) ||
            billingLoc ||
            locations[0];

        return { billingAddress: formatPracticeLocationLine(loc) };
    }, [getSelectedPractice, practiceLocationsInfo, watchedValues?.practiceLocationId]);

    /**
     * The pharmacy this Rx goes to. Every user-facing string names it from the product
     * instead of hardcoding one pharmacy, so the copy is correct for whichever pharmacy
     * the provider picked. Phrased to sit mid-sentence so the fallback still reads.
     */
    const pharmacyDisplayName = product?.manufacturerName || "the pharmacy";

    // CS-2075 — existing-patient phone is only required/shown for Genesis enqueue
    const isGenesisPharmacy = resolvePharmacyKey(product?.manufacturerName) === PHARMACY_CONFIG_KEY_GENESIS;
    const isMandatoryAgreement =
        product?.agreementMode === PRODUCT_CONFIG_VALUE.MANDATORY || !!product?.isAgreementRequired;
    const shouldFilterPracticesByExecutedAgreement = useExecutedAgreementPracticeRules
        ? isMandatoryAgreement || isGenesisPharmacy
        : fromExploreMarket && isGenesisPharmacy;
    const agreementPracticesLoading =
        shouldFilterPracticesByExecutedAgreement &&
        (genesisAgreementPracticesLoading ||
            (useExecutedAgreementPracticeRules && !userPracticesLoaded));

    useEffect(() => {
        if (!isOpen || !shouldFilterPracticesByExecutedAgreement || !product?.productId) {
            setGenesisAgreementPractices([]);
            setGenesisAgreementPracticesLoading(false);
            return;
        }

        let cancelled = false;
        const loadGenesisAgreementPractices = async () => {
            setGenesisAgreementPracticesLoading(true);
            try {
                const response = await getRxAgreementPracticeLookupService(
                    [Number(product.productId)],
                    true,
                );
                const lookupList = response?.data?.data ?? [];
                const practicesById = new Map<number, string>();
                lookupList.forEach((practice: { practiceId?: number; practiceName?: string }) => {
                    if (practice?.practiceId == null) return;
                    practicesById.set(
                        Number(practice.practiceId),
                        practice.practiceName || String(practice.practiceId),
                    );
                });
                // CS-2075 — user-practice filter moved to the genesisAgreementPractices memo.
                const practices = Array.from(practicesById.entries())
                    .map(([practiceId, practiceName]) => ({ practiceId, practiceName }));
                if (!cancelled) {
                    setGenesisAgreementPractices(practices);
                }
            } catch {
                if (!cancelled) {
                    setGenesisAgreementPractices([]);
                }
            } finally {
                if (!cancelled) {
                    setGenesisAgreementPracticesLoading(false);
                }
            }
        };
        void loadGenesisAgreementPractices();
        return () => {
            cancelled = true;
        };
    }, [
        isOpen,
        shouldFilterPracticesByExecutedAgreement,
        product?.productId,
        // practiceByUserRoleUserSlice, // CS-2075 — filter is the memo below; refetching here caused the flicker
    ]);

    // CS-2075 — lookup ∩ user's practices (empty user list = no filter, as before).
    const genesisAgreementPractices = useMemo(() => {
        if (useExecutedAgreementPracticeRules && shouldFilterPracticesByExecutedAgreement && !userPracticesLoaded) {
            return [];
        }
        const userPracticeIds = new Set(
            (practiceByUserRoleUserSlice ?? []).map((practice) => Number(practice.practiceId)),
        );
        return genesisAgreementLookup.filter(
            (practice) => userPracticeIds.size === 0 || userPracticeIds.has(practice.practiceId),
        );
    }, [
        genesisAgreementLookup,
        practiceByUserRoleUserSlice,
        shouldFilterPracticesByExecutedAgreement,
        useExecutedAgreementPracticeRules,
        userPracticesLoaded,
    ]);

    const pharmacyConfig = useMemo(() => {
        const manufacturerId = Number(product?.manufacturerId);
        if (!manufacturerId) return undefined;
        return pharmacyInfoList?.find((pharmacy) => Number(pharmacy.manufacturerId) === manufacturerId)?.miscConfig;
    }, [pharmacyInfoList, product?.manufacturerId]);
    const billToOptions = useMemo(() => pharmacyBillToRadioOptions(pharmacyConfig), [pharmacyConfig]);
    const defaultBillTo = useMemo(() => pharmacyBillToRxDefault(pharmacyConfig), [pharmacyConfig]);

    React.useEffect(() => {
        if (step !== 2 || !billToOptions.length) return;
        const allowed = billToOptions.map((option) => option.value);
        const current = getValues("billTo");
        if (!current || !allowed.includes(current)) {
            setValue("billTo", defaultBillTo);
        }
    }, [step, billToOptions, defaultBillTo, getValues, setValue]);

    const isSterileProduct = useMemo(
        () => isSterileCompoundProduct({
            compoundType: product?.compoundType,
        }),
        [product?.compoundType]
    );

    useEffect(() => {
        setAgreementSkuPrices(null); // CS-2075 — cleared on every run; set below only from this practice's EXECUTED row
        if (!isOpen) {
            setAgreementUnitExtendedPrice(null);
            return;
        }
        const practiceId = Number(watchedValues?.practiceId);
        if (!practiceId || !product?.productId) {
            setAgreementUnitExtendedPrice(null);
            return;
        }

        let cancelled = false;
        const loadAgreementUnitPrice = async () => {
            const action = await dispatch(
                fetchPracticeProductsDataUsingDateOfServiceAndProvider({
                    practiceId,
                    dateOfService: moment().format("MM/DD/YYYY"),
                    providerId: 0,
                    moduleName: "",
                    includeRxRequiredProducts: false,
                    skipLoader: true,
                }),
            );
            if (cancelled) return;
            if (!fetchPracticeProductsDataUsingDateOfServiceAndProvider.fulfilled.match(action)) {
                setAgreementUnitExtendedPrice(null);
                return;
            }
            const rows = Array.isArray(action.payload?.data) ? action.payload.data : null;
            if (!rows) {
                setAgreementUnitExtendedPrice(null);
                return;
            }
            const agreementRow = rows.find(
                (row: { productId?: number; agreementStatus?: string }) =>
                    Number(row.productId) === Number(product.productId) &&
                    String(row.agreementStatus ?? "").trim().toUpperCase() === "EXECUTED",
            );
            setAgreementUnitExtendedPrice(
                parseAgreementUnitExtendedPrice(
                    (agreementRow as { extendedPrice?: unknown; discountedPrice?: unknown })
                        ?.extendedPrice ??
                        (agreementRow as { discountedPrice?: unknown })?.discountedPrice,
                ),
            );
            // CS-2075 START — SKU pricing, resolved the same way as the Order screen
            const skuRow = agreementRow as {
                skuAllowablePrices?: any[] | null;
                skuExtendedPrices?: any[] | null;
                hasSKUPrice?: boolean;
                hasSku?: boolean;
            } | undefined;
            const skus = skuRow?.skuAllowablePrices ?? skuRow?.skuExtendedPrices ?? null;
            const hasSkuList = Array.isArray(skus) && skus.length > 0;
            const hasSku = (skuRow?.hasSKUPrice ?? skuRow?.hasSku ?? false) || hasSkuList;
            setAgreementSkuPrices(hasSku && hasSkuList ? skus : null);
            // CS-2075 END
        };
        void loadAgreementUnitPrice();
        return () => {
            cancelled = true;
        };
    }, [dispatch, isOpen, product?.productId, watchedValues?.practiceId]);

    /** Gate 1 only — keyed to ship-to state (and office-use when ship-to is practice). */
    const pharmacyShipToGateResult: GatingResult = useMemo(() => {
        const shipTo = watchedValues?.shipTo;
        const shipToState = watchedValues?.shipToAddress?.state;
        if (!shipTo || !shipToState) {
            return { passed: true, failures: [] };
        }

        const input: RxOrderGatingInput = {
            pharmacyId: gatePharmacyId,
            pharmacyDisplayName: gatePharmacyName,
            shipToState,
            shipToPractice: shipTo === "clinic",
            isSterileCompound: isSterileProduct,
            pharmacyLicenses,
        };
        return runPharmacyShipToGate(input);
    }, [
        watchedValues?.shipTo,
        watchedValues?.shipToAddress?.state,
        isSterileProduct,
        pharmacyLicenses,
        gatePharmacyId,
        gatePharmacyName,
    ]);

    const shipToPharmacyFailures = pharmacyShipToGateResult.failures;

    useEffect(() => {
        if (
            step === 2 &&
            !isGenesisPharmacy &&
            !isPracticeRegisteredWithPharmacy &&
            watchedValues.billTo === "clinic"
        ) {
            setShowPharmacyBillingSetupPopup(true);
        } else {
            setShowPharmacyBillingSetupPopup(false);
        }
    }, [step, isGenesisPharmacy, isPracticeRegisteredWithPharmacy, watchedValues.billTo]);

    const isValidPhone = (value: string | undefined) =>
        (value || "").replace(/\D/g, "").length === 10;

const canProceed = useMemo(() => {
    if (step === 0) {
        const patientOk = isNewPatient
            ? !!(watchedValues?.newPatient?.firstName &&
                 watchedValues?.newPatient?.lastName &&
                 watchedValues?.newPatient?.birthDate &&
                 watchedValues?.newPatient?.gender &&
                 isValidPhone(watchedValues?.newPatient?.phone) &&
                 watchedValues?.newPatient?.patientAddressInfo?.patientStreetAddress &&
                 watchedValues?.newPatient?.patientAddressInfo?.patientCity &&
                 watchedValues?.newPatient?.patientAddressInfo?.patientState &&
                 watchedValues?.newPatient?.patientAddressInfo?.patientState)
            : !!(watchedValues?.patientId &&
                 watchedValues?.patientDetails?.firstName &&
                 watchedValues?.patientDetails?.lastName &&
                 watchedValues?.patientDetails?.birthDate &&
                 watchedValues?.patientDetails?.gender &&
                 (!isGenesisPharmacy || isValidPhone(watchedValues?.patientDetails?.phone)));

        return !!watchedValues?.practiceId && patientOk;
    }

    if (step === 1) {
        return !!watchedValues?.providerId && !!watchedValues?.practiceLocationId;
    }

    if (step === 2) {
        const billToSelected = !!watchedValues?.billTo && !!watchedValues?.shipTo;

        if (
            !isGenesisPharmacy &&
            watchedValues?.billTo === "clinic" &&
            !isPracticeRegisteredWithPharmacy
        ) {
            return false;
        }

        if (!billToSelected) return false;

        const billPhoneOk = isValidPhone(watchedValues?.billToAddress?.phone);
        const shipPhoneOk = isValidPhone(watchedValues?.shipToAddress?.phone);
        if (!billPhoneOk || !shipPhoneOk) return false;

        // Hard-block Continue when Gate 1 fails for the selected ship-to state
        if (watchedValues?.shipToAddress?.state && !pharmacyShipToGateResult.passed) {
            return false;
        }

        return true;
    }

    if (step === 3) {
        return Object.keys(selectedDosages).length > 0;
    }

    if (step === 4) {
        return allergies.trim().length > 0;
    }

    return true;
}, [watchedValues, step, selectedDosages, allergies, isNewPatient, isPracticeRegisteredWithPharmacy, pharmacyShipToGateResult.passed, isGenesisPharmacy]);

    const getFieldsForStep = (stepNum: number): string[] => {
        if (stepNum === 0) {
            const fields = ['practiceId'];
            if (isNewPatient) {
                fields.push(
                    'newPatient.firstName',
                    'newPatient.lastName',
                    'newPatient.birthDate',
                    'newPatient.gender',
                    'newPatient.phone',
                );
            } else {
                fields.push('patientId');
                if (isGenesisPharmacy) {
                    fields.push('patientDetails.phone');
                }
            }
            return fields;
        }
        if (stepNum === 1) return ['providerId', 'practiceLocationId'];
        if (stepNum === 2) {
            const fields = ['billTo', 'shipTo'];
            if (getValues('billTo')) fields.push('billToAddress.phone');
            if (getValues('shipTo')) fields.push('shipToAddress.phone');
            return fields;
        }
        return [];
    };

    const validationAttemptedOnCurrentStep = !!stepValidationAttempted[step];

    useEffect(() => {
        if (submitted || !validationAttemptedOnCurrentStep) return undefined;
        const subscription = watch(() => {
            const fields = getFieldsForStep(step);
            if (fields.length > 0) void trigger(fields as any);
        });
        return () => subscription.unsubscribe();
    }, [step, validationAttemptedOnCurrentStep, submitted, watch, trigger]);

    const focusFirstError = () => {
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                focusFirstValidationErrorInContainer(prescriptionDialogPaperRef.current);
            });
        });
    };

    const openGatingBlock = (failures: GatingFailure[]) => {
        setGatingFailures(failures);
        setShowGatingBlockPopup(true);
        void logGate1BlockedAttempts(failures, "hard_block");
    };

    const buildGatingBlockAuditKey = (failures: GatingFailure[]): string => {
        const shipTo = getValues("shipTo") || "";
        const shipToState = (getValues("shipToAddress.state") || "").toUpperCase();
        const codes = failures.map((f) => f.code).sort().join(",");
        return `${shipTo}|${shipToState}|${codes}|${product?.productId || ""}`;
    };

    const logGate1BlockedAttempts = async (
        failures: GatingFailure[],
        source: "inline_banner" | "hard_block"
    ) => {
        if (!failures?.length) return;

        const key = `${source}|${buildGatingBlockAuditKey(failures)}`;
        if (lastLoggedGatingBlockKeyRef.current === key) return;
        lastLoggedGatingBlockKeyRef.current = key;

        const shipTo = getValues("shipTo") || "";
        const shipToState = (getValues("shipToAddress.state") || "").toUpperCase();
        const practiceId = Number(getValues("practiceId")) || null;
        const patientId = isNewPatient ? null : Number(getValues("patientId")) || null;
        const providerId = Number(getValues("providerId")) || null;
        const locationId = Number(getValues("practiceLocationId")) || null;
        const first = getValues("providerDetails.firstName") || "";
        const last = getValues("providerDetails.lastName") || "";
        const providerName = `${first} ${last}`.trim() || null;
        const npi = getValues("providerDetails.npi") || null;
        const compoundType =
            (product?.compoundType || "").trim() ||
            (isSterileProduct ? "sterile" : "non-sterile");
        const timestamp = new Date().toISOString();
        const reasonText = failures.map((f) => f.message).join(" | ");

        const productId = product?.productId ?? null;
        const request: RxGatingBlockedAttemptRequest = {
            entityName: "PRESCRIPTION",
            entityNumber: blockedPrescriptionId
                ? `PRESCRIPTION-${blockedPrescriptionId}`
                : productId
                    ? `PRODUCT-${productId}`
                    : "PRESCRIPTION-PENDING",
            entityId: blockedPrescriptionId,
            prescriptionId: blockedPrescriptionId,
            actionType: "BLOCKED",
            action: `${PRESCRIPTION_ACTIONS.RX_GATING_BLOCKED}: Gate 1 pharmacy license check failed. Reason: ${reasonText}`,
            auditTimestamp: timestamp,
            auditedObject: {
                timestamp,
                userName: userObject?.email || email || null,
                practiceId,
                patientId,
                locationId,
                productId,
                productName: product?.productName ?? null,
                pharmacy: {
                    pharmacyId: gatePharmacyId,
                    pharmacyName: gatePharmacyName,
                },
                prescriber: {
                    providerId,
                    providerName,
                    npi,
                },
                state: shipToState || failures[0]?.state || "",
                shipTo: shipTo || null,
                shipToPractice: shipTo === "clinic",
                compoundType,
                isSterileCompound: isSterileProduct,
                reasons: failures.map((f) => ({
                    code: f.code,
                    message: f.message,
                    state: f.state,
                })),
            },
        };

        try {
            const result: any = await dispatch(logRxGatingBlockedAttempt({ request, skipLoader: true }));
            const createdId =
                result?.payload?.data?.prescriptionId
                ?? result?.payload?.prescriptionId
                ?? null;
            if (createdId != null && !Number.isNaN(Number(createdId))) {
                setBlockedPrescriptionId(Number(createdId));
            }
        } catch (error) {
            // Compliance logging must not block the user from seeing the Gate 1 error.
            console.error("Failed to log Rx gating blocked attempt:", error);
        }
    };

    // Compliance: log every distinct Gate 1 inline block (e.g. NY office-use banner).
    useEffect(() => {
        if (shipToPharmacyFailures.length === 0) return;
        void logGate1BlockedAttempts(shipToPharmacyFailures, "inline_banner");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shipToPharmacyFailures]);

    const getPatientStateCode = (): string => {
        const shipTo = getValues("shipTo") || "";
        const shipToState = getValues("shipToAddress.state") || "";
        return resolvePatientState({
            shipTo,
            shipToState,
            patientResidencyState: resolvePatientResidencyState(),
        });
    };

    const getStateDisplayLabel = (stateCode: string): string => {
        const code = (stateCode || "").trim().toUpperCase();
        if (!code) return "";
        const match = stateList.find((s) => String(s.keyName).toUpperCase() === code);
        return match?.displayValue || code;
    };

    const buildPrescriberAcknowledgmentText = (
        providerName: string,
        npi: string,
        patientStateLabel: string
    ): string =>
        `I, ${providerName}, NPI ${npi}, am licensed to prescribe in ${patientStateLabel} where this patient is located, this prescription is patient-specific and medically necessary, and I am responsible for compliance with all applicable laws.`;

    const resolvePatientResidencyState = (): string => {
        if (isNewPatient) {
            return getValues("newPatient.patientAddressInfo.patientState") || "";
        }
        if (!selectedPatientDetails) return "";
        const responseArray = Array.isArray(selectedPatientDetails)
            ? selectedPatientDetails
            : [selectedPatientDetails];
        const patientInfo = responseArray[0]?.data?.patientInfo;
        if (!patientInfo?.length) return "";
        const addressRecord = patientInfo.find(
            (record: any) => record.infoType === "ADDRESS" && record.inactiveDate === null
        );
        return addressRecord?.patientinfoData?.patientState || "";
    };

    const buildFullGatingInput = (): RxOrderGatingInput => {
        const shipTo = getValues("shipTo") || "";
        const shipToState = getValues("shipToAddress.state") || "";

        return {
            pharmacyId: gatePharmacyId,
            pharmacyDisplayName: gatePharmacyName,
            shipToState,
            shipToPractice: shipTo === "clinic",
            isSterileCompound: isSterileProduct,
            pharmacyLicenses,
        };
    };

    const clearPrescriberConfirmation = () => {
        setPrescriberConfirmation(null);
        setPrescriberConfirmChecked(false);
        setShowPrescriberConfirmPopup(false);
    };

    const handlePrescriberConfirmContinue = async () => {
        if (!prescriberConfirmChecked || isLoggingPrescriberConfirmation) return;

        const first = getValues("providerDetails.firstName") || "";
        const last = getValues("providerDetails.lastName") || "";
        const providerName = `${first} ${last}`.trim() || "Provider";
        const npi = getValues("providerDetails.npi") || "";
        const patientState = getPatientStateCode();
        const patientStateLabel = getStateDisplayLabel(patientState) || patientState || "the patient state";
        const acknowledgmentText = buildPrescriberAcknowledgmentText(
            providerName,
            npi || "—",
            patientStateLabel
        );

        const confirmation: PrescriberConfirmation = {
            confirmed: true,
            confirmedAt: new Date().toISOString(),
            providerId: Number(getValues("providerId")) || 0,
            providerName,
            npi,
            patientState,
            acknowledgmentText,
        };

        const practiceId = Number(getValues("practiceId")) || null;
        const patientId = isNewPatient ? null : Number(getValues("patientId")) || null;
        const locationId = Number(getValues("practiceLocationId")) || null;
        const productId = product?.productId ?? null;
        const timestamp = confirmation.confirmedAt;

        const request: LogPrescriberConfirmationRequest = {
            entityName: "PRESCRIPTION",
            entityNumber: blockedPrescriptionId
                ? `PRESCRIPTION-${blockedPrescriptionId}`
                : productId
                    ? `PRODUCT-${productId}`
                    : "PRESCRIPTION-PENDING",
            entityId: blockedPrescriptionId,
            prescriptionId: blockedPrescriptionId,
            actionType: "MODIFY",
            action: `${PRESCRIPTION_ACTIONS.PRESCRIBER_CONFIRMATION}: Provider acknowledged Gate 2 confirmation for patient state ${patientState || "—"}.`,
            auditTimestamp: timestamp,
            auditedObject: {
                timestamp,
                userName: userObject?.email || email || null,
                practiceId,
                patientId,
                locationId,
                productId,
                productName: product?.productName ?? null,
                providerId: confirmation.providerId || null,
                providerName: confirmation.providerName,
                npi: confirmation.npi || null,
                patientState: confirmation.patientState || null,
                acknowledgmentText: confirmation.acknowledgmentText,
                confirmed: true,
                confirmedAt: confirmation.confirmedAt,
            },
        };

        setIsLoggingPrescriberConfirmation(true);
        try {
            const result: any = await dispatch(logPrescriberConfirmation({ request, skipLoader: true }));
            if (
                result?.meta?.requestStatus !== "fulfilled"
                || (result?.payload?.code && result.payload.code !== SUCCESS_CODE)
            ) {
                dispatch(setSingleAlertObj({
                    message: "Unable to log prescriber confirmation. Please try again before submitting.",
                    alertType: "error",
                }));
                return;
            }

            // Prefer the draft id returned by the audit API. Do not rely on setState before submit —
            // React state updates are async and handleFinalSubmit would otherwise submit without
            // prescriptionId, creating a new row and orphaning the confirmation audit.
            const createdId =
                result?.payload?.data?.prescriptionId
                ?? result?.payload?.prescriptionId
                ?? null;
            const draftPrescriptionId =
                createdId != null && !Number.isNaN(Number(createdId))
                    ? Number(createdId)
                    : blockedPrescriptionId;
            if (draftPrescriptionId != null) {
                setBlockedPrescriptionId(draftPrescriptionId);
            }

            // Audit trail must be written before submit is allowed to proceed.
            setPrescriberConfirmation(confirmation);
            setShowPrescriberConfirmPopup(false);
            setPrescriberConfirmChecked(false);
            void handleFinalSubmit(confirmation, draftPrescriptionId);
        } catch (error) {
            console.error("Failed to log prescriber confirmation:", error);
            dispatch(setSingleAlertObj({
                message: "Unable to log prescriber confirmation. Please try again before submitting.",
                alertType: "error",
            }));
        } finally {
            setIsLoggingPrescriberConfirmation(false);
        }
    };

    const handleNextStep = async () => {
        if (canProceed) {
            const fields = getFieldsForStep(step);
            if (fields.length > 0) clearErrors(fields as any);
            setStepValidationAttempted((prev) => {
                const next = { ...prev };
                delete next[step];
                return next;
            });
            setStep(step + 1);
            return;
        }

        if (
            step === 2 &&
            !isGenesisPharmacy &&
            watchedValues?.billTo === "clinic" &&
            !isPracticeRegisteredWithPharmacy
        ) {
            setShowPharmacyBillingSetupPopup(true);
            return;
        }

        if (
            step === 2 &&
            watchedValues?.shipToAddress?.state &&
            !pharmacyShipToGateResult.passed
        ) {
            openGatingBlock(pharmacyShipToGateResult.failures);
            return;
        }

        setStepValidationAttempted(prev => ({ ...prev, [step]: true }));
        const fields = getFieldsForStep(step);
        if (fields.length > 0) {
            await trigger(fields as any);
        }
        focusFirstError();
    };

    const handleSubmitClick = async () => {
        if (canProceed) {
            const gating = runPharmacyShipToGate(buildFullGatingInput());
            if (!gating.passed) {
                openGatingBlock(gating.failures);
                return;
            }
            if (!prescriberConfirmation?.confirmed) {
                setPrescriberConfirmChecked(false);
                setShowPrescriberConfirmPopup(true);
                return;
            }
            handleFinalSubmit(prescriberConfirmation);
            return;
        }
        setStepValidationAttempted(prev => ({ ...prev, [step]: true }));
        const fields = getFieldsForStep(step);
        if (fields.length > 0) {
            await trigger(fields as any);
        }
        focusFirstError();
    };

    const handleBack = () => {
        if (step <= 0) return;
        const prev = step - 1;
        const fields = getFieldsForStep(prev);
        if (fields.length > 0) clearErrors(fields as any);
        setStepValidationAttempted((s) => {
            const next = { ...s };
            delete next[prev];
            return next;
        });
        setStep(prev);
    };

    const toggleDosage = (d: ProductDosageInfo, checked: boolean) => {
        setSelectedDosages((prev) => {
            const next = { ...prev };
            if (checked) {
                next[d.dosageId] = { quantity: parseInt(String(d.quantity)) || 1, refills: parseInt(String(d.refill)) || 0, sig: d.directions }; // CS-2075
            } else {
                delete next[d.dosageId];
            }
            return next;
        });
    };

    const updateDosageField = <K extends keyof SelectedDosage>(id: number, field: K, value: SelectedDosage[K]) => {
        setSelectedDosages((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
    };

    const handlePharmacyFormDownload = () => {
        const providerId = Number(getValues('providerId')) || undefined;
        const practiceId = Number(getValues('practiceId'));

        dispatch(sendNotificationEmail({ providerId, practiceId, skipLoader: true }));

        const link = document.createElement('a');
        link.href = '/forms/sandsrx_account_setup_form.pdf';
        link.download = 'sandsrx_account_setup_form.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handlePreviewPrescription = async (documentId: any, drugName: string) => {
        if (!documentId) return;
        setPreviewingId(documentId);
        try {
            const actionResult = await dispatch(
                fetchOrderDocumentsByIds({ documentIds: documentId, skipLoader: true })
            );
            const docData = actionResult.payload;
            if (docData?.content) {
                setPdfContent(docData.content);
                setShowPDF({ isShow: true, title: drugName, fileName: drugName });
            } else {
                dispatch(setSingleAlertObj({
                    message: "Requested document was not found on the server.",
                    alertType: "info",
                }));
            }
        } finally {
            setPreviewingId(null);
        }
    };

    const handleFinalSubmit = async (
        confirmationOverride?: PrescriberConfirmation | null,
        prescriptionIdOverride?: number | null
    ) => {
        const confirmation = confirmationOverride ?? prescriberConfirmation;
        if (!confirmation?.confirmed) {
            setPrescriberConfirmChecked(false);
            setShowPrescriberConfirmPopup(true);
            return;
        }

        // Gate 1 re-check immediately before submit (ship-to may have changed).
        const gating = runPharmacyShipToGate(buildFullGatingInput());
        if (!gating.passed) {
            openGatingBlock(gating.failures);
            return;
        }

        // Use explicit override when provided.
        const draftPrescriptionId =
            prescriptionIdOverride !== undefined ? prescriptionIdOverride : blockedPrescriptionId;
        const productPayload = productDosageList
            .filter((d) => selectedDosages[d.dosageId])
            .map((d) => ({
                drugName: d.monthAndDrugName,
                medicationNdc: null,
                externalId: d.externalId,
                prescribedItemId: d.dosageId,
                quantity: selectedDosages[d.dosageId].quantity,
                unit: "each",
                refills: selectedDosages[d.dosageId].refills,
                sig: selectedDosages[d.dosageId].sig,
                directions: selectedDosages[d.dosageId].sig,
                notes: d.dosageNote,
            }));

        const selectedPractice = practiceByUserRoleUserSlice?.find(
            (p: any) => String(p.practiceId) === String(getValues('practiceId'))
        );

        const selectedLocation = practiceLocationsInfo?.find(
            (l: any) => l.locationId === Number(getValues('practiceLocationId'))
        );

        const editedPatientAddress = getValues('billTo') === 'patient'
            ? getValues('billToAddress')
            : getValues('shipTo') === 'patient'
                ? getValues('shipToAddress')
                : null;

        const editedPracticeAddress = getValues('billTo') === 'clinic'
            ? getValues('billToAddress')
            : getValues('shipTo') === 'clinic'
                ? getValues('shipToAddress')
                : null;

        const patientDataRequest = isNewPatient
            ? {
                patientId: null,
                firstName: getValues('newPatient.firstName') || '',
                middleName: '',
                lastName: getValues('newPatient.lastName') || '',
                birthDate: getValues('newPatient.birthDate') || '',
                gender: getValues('newPatient.gender') || '',
                phone: getValues('newPatient.phone') || '',
                allergies: allergies || null,
                patientActiveMedication: medicationNote.trim() ? medicationNote.trim() : null,
                patientAddressInfo: {
                    patientStreetAddress: editedPatientAddress?.address || getValues('newPatient.patientAddressInfo.patientStreetAddress') || '',
                    patientUnitNumber: getValues('newPatient.patientAddressInfo.patientUnitNumber') || '',
                    patientCity: editedPatientAddress?.city || getValues('newPatient.patientAddressInfo.patientCity') || '',
                    patientState: editedPatientAddress?.state || getValues('newPatient.patientAddressInfo.patientState') || '',
                    patientZipCode: editedPatientAddress?.zip || getValues('newPatient.patientAddressInfo.patientZipCode') || '',
                },
            }
            : {
                patientId: Number(getValues('patientId')) || null,
                firstName: getValues('patientDetails.firstName') || '',
                middleName: getValues('patientDetails.middleName') || '',
                lastName: getValues('patientDetails.lastName') || '',
                birthDate: getValues('patientDetails.birthDate') || '',
                gender: getValues('patientDetails.gender') || '',
                phone: getValues('patientDetails.phone') || '',
                allergies: allergies || null,
                patientActiveMedication: medicationNote.trim() ? medicationNote.trim() : null,
                patientAddressInfo: {
                    patientStreetAddress: editedPatientAddress?.address || '',
                    patientUnitNumber: '',
                    patientCity: editedPatientAddress?.city || '',
                    patientState: editedPatientAddress?.state || '',
                    patientZipCode: editedPatientAddress?.zip || '',
                },
            };

        const providerDateRequet = {
            providerId: Number(getValues('providerId')),
            npi: getValues('providerDetails.npi') || '',
            first_name: getValues('providerDetails.firstName') || '',
            last_name: getValues('providerDetails.lastName') || '',
            dea: getValues('providerDetails.dea') || '',
        };

        const practiceDataRequest = {
            practiceId: Number(getValues('practiceId')),
            practiceLocationId: Number(getValues('practiceLocationId')),
            practiceName: selectedPractice?.practiceName || '',
            phone: selectedPractice?.contactPhone || '',
            practiceAddressInfo: {
                streetAddress: editedPracticeAddress?.address || selectedLocation?.streetAddress || '',
                unitNumber: selectedLocation?.unitNumber || '',
                city: editedPracticeAddress?.city || selectedLocation?.city || '',
                state: editedPracticeAddress?.state || selectedLocation?.state || '',
                zipCode: editedPracticeAddress?.zip || selectedLocation?.zipCode || '',
            },
        };

        const payload: PrescriptionSubmitRequest = {
            productId: product.productId,
            patientDataRequest,
            providerDateRequet,
            practiceDataRequest,
            productPayload,
            billTo: getValues('billTo'),
            shipTo: getValues('shipTo'),
            notes: null,
            prescriberConfirmation: confirmation,
            prescriptionId: draftPrescriptionId,
        };
        
        setSubmitted(true);
        setSubmitting(true);
        setSubmittedPrescriptions([]);
        setPdfContent("");

        setTimeout(() => {
            dispatch(showHideLoadingDialog(false));
        }, 0);

        if (onSubmitProp) {
            try {
                const prescriptionsFromResponse = await onSubmitProp(payload);
                if (prescriptionsFromResponse && prescriptionsFromResponse.length > 0) {
                    setSubmittedPrescriptions(prescriptionsFromResponse);
                    setBlockedPrescriptionId(null);
                } else {
                    // API failed or returned no prescriptions (e.g. error modal); leave the wizard on the last step
                    setSubmitted(false);
                }
            } catch (error) {
                console.error("Prescription submission failed:", error);
                setSubmitted(false);
            } finally {
                setSubmitting(false);
                dispatch(showHideLoadingDialog(false));
            }
        } else {
            setSubmitting(false);
            dispatch(showHideLoadingDialog(false));
        }
    };


    const FieldLabel = ({ label, required = false }: { label: string; required?: boolean }) => (
        <Typography sx={labelSx}>
            {label}
            {required && <Box component="span" sx={{ color: tokens.accent }}>*</Box>}
        </Typography>
    );

    useEffect(() => {
        if (loadingComplete) {
            dispatch(showHideLoadingDialog(false))
        }
    }, [loadingComplete])

    useEffect(() => {
        const fetchPractices = async () => {
            try {
                const response = await dispatch(getPracticesByUserRoleUserSlice(true));
                if (response) {
                    setLoadingComplete(true);
                }
            } catch (error) {
                console.error("Failed to fetch practices:", error);
                setLoadingComplete(true);
            } finally {
                if (useExecutedAgreementPracticeRules) {
                    setUserPracticesLoaded(true);
                }
            }
        };

        const fetchProductDosageList = async () => {
            try {
                const response = await dispatch(fetchAvailableDrugDosabeByProduct({ productId: product.productId }));
                console.log(response)
                if (response) {
                    setLoadingComplete(true);
                }
            } catch (error) {
                console.error("Failed to fetch practices:", error);
                setLoadingComplete(true);
            }
        }

        fetchPractices();
        fetchProductDosageList();
        dispatch(fetchPharmaciesList({ skipLoader: true }));
        // CS-2073 — load pharmacy_licenses for Gate 1 from product manufacturer
        dispatch(
            fetchPharmacyLicenses({
                manufacturerId: product.manufacturerId,
                manufacturerName: product.manufacturerName,
            })
        );
    }, [isOpen, product.manufacturerId, product.manufacturerName]);

    // Pre-populate practice from OrderProductModal selection
    useEffect(() => {
        if (!initialPracticeId || !practiceByUserRoleUserSlice?.length) return;

        const match = practiceByUserRoleUserSlice.find(
            (p: any) => Number(p.practiceId) === initialPracticeId
        );
        if (!match) return;

        setValue("practiceId", initialPracticeId);
        // CS-2075 — the watch(practiceId) effect loads these once; calling here too fired each API 2-3x.
        // loadPatientsForPractice(String(initialPracticeId));
        // loadPracticeProvidersForPractice(String(initialPracticeId));
        // loadPracticeLocationsForPractice(String(initialPracticeId));
    }, [initialPracticeId, practiceByUserRoleUserSlice]);

    // Pre-populate patient from OrderProductModal selection
    useEffect(() => {
        if (!initialPatientId || !patientList?.length) return;

        const match = patientList.find(
            (p: any) => Number(p.patientId) === initialPatientId
        );
        if (!match) return;

        setValue("patientId", initialPatientId);
    }, [initialPatientId, patientList]);

    // Single active patient for the selected practice — default the dropdown.
    useEffect(() => {
        if (isNewPatient || loadingPatients) return;
        const practiceId = getValues("practiceId");
        if (!practiceId || !patientList?.length || patientList.length !== 1) return;

        const solePatientId = Number(patientList[0].patientId);
        if (initialPatientId != null && Number(initialPatientId) !== solePatientId) return;

        const currentPatientId = getValues("patientId");
        if (currentPatientId && Number(currentPatientId) === solePatientId) return;

        setValue("patientId", solePatientId);
    }, [
        isNewPatient,
        loadingPatients,
        patientList,
        watchedValues.practiceId,
        initialPatientId,
        setValue,
        getValues,
    ]);

    const practiceOptions = useMemo(() => {
        const options: Option[] = [];
        const practicesSource = shouldFilterPracticesByExecutedAgreement
            ? genesisAgreementPractices
            : practiceByUserRoleUserSlice ?? [];

        if (!practicesSource?.length) return options;

        if (practicesSource.length === 1) {
            const only = practicesSource[0];
            options.push({
                value: String(only.practiceId),
                label: only.practiceName,
            });
            setValue("practiceId", Number(options[0].value));
            return options;
        }

        practicesSource.forEach((practice) => {
            options.push({
                value: String(practice.practiceId),
                label: practice.practiceName,
            });
        });
        return options;
    }, [
        genesisAgreementPractices,
        practiceByUserRoleUserSlice,
        setValue,
        shouldFilterPracticesByExecutedAgreement,
    ]);

    useEffect(() => {
        if (!isOpen || !shouldFilterPracticesByExecutedAgreement) return;
        const allowedIds = new Set(genesisAgreementPractices.map((practice) => practice.practiceId));
        const currentPracticeId = Number(getValues("practiceId"));
        if (currentPracticeId && allowedIds.size > 0 && !allowedIds.has(currentPracticeId)) {
            resetField("practiceId");
        }
    }, [
        genesisAgreementPractices,
        getValues,
        isOpen,
        resetField,
        shouldFilterPracticesByExecutedAgreement,
    ]);

    useEffect(() => {
        if (!isOpen || !shouldFilterPracticesByExecutedAgreement || lockPracticeSelection) return;
        if (genesisAgreementPractices.length === 1) {
            setValue("practiceId", Number(genesisAgreementPractices[0].practiceId));
        }
    }, [
        genesisAgreementPractices,
        isOpen,
        lockPracticeSelection,
        setValue,
        shouldFilterPracticesByExecutedAgreement,
    ]);

    useEffect(() => {
        // const practiceId = getValues('practiceId');
        // CS-2075 — read the watched value this effect is keyed on; getValues() could already hold a value set
        // earlier in the same commit, loading once here and again when the watched value caught up.
        const practiceId = watchedValues.practiceId;
        if (!practiceId) return;

        loadPatientsForPractice(practiceId.toString());
        loadPracticeProvidersForPractice(practiceId.toString());
        loadPracticeLocationsForPractice(practiceId.toString());
    }, [watchedValues.practiceId]);

    const practiceProvidersOptions = useMemo(() => {
        let options: { label: string; value: number }[] = [];
        practiceProvidersInfo?.map((option: any) => {
            const name = `${option.title ? option.title + " " : ""}${option.firstName ? option.firstName : ""} ${option.middleName ? option.middleName + " " : ""}${option.lastName ? option.lastName : ""}`.trim();
            const npiSuffix = option.npi ? ` - ${option.npi}` : "";
            options.push({
                label: `${name}${npiSuffix}`,
                value: option.providerId,
            });
        });
        return options;
    }, [practiceProvidersInfo]);

    // Single active provider for the selected practice — default the dropdown.
    useEffect(() => {
        if (loadingProviders) return;
        const practiceId = getValues("practiceId");
        if (!practiceId || practiceProvidersOptions.length !== 1) return;

        const soleProviderId = practiceProvidersOptions[0].value;
        const currentProviderId = getValues("providerId");
        if (currentProviderId && Number(currentProviderId) === Number(soleProviderId)) return;

        setValue("providerId", soleProviderId);
    }, [
        loadingProviders,
        practiceProvidersOptions,
        watchedValues.practiceId,
        setValue,
        getValues,
    ]);

    // Single schedule/dosage row for this product — select it by default.
    useEffect(() => {
        if (!isOpen || !productDosageList?.length) return;
        if (productDosageList.length !== 1) {
            scheduleDosageAutoSelectDoneRef.current = false;
            return;
        }

        const dosage = productDosageList[0];
        if (selectedDosages[dosage.dosageId]) return;
        if (scheduleDosageAutoSelectDoneRef.current) return;

        setSelectedDosages({
            [dosage.dosageId]: {
                quantity: parseInt(String(dosage.quantity)) || 1,
                refills: parseInt(String(dosage.refill)) || 0, // CS-2075
                sig: dosage.directions,
            },
        });
        scheduleDosageAutoSelectDoneRef.current = true;
    }, [isOpen, productDosageList, selectedDosages]);

    const fetchStreetSuggestions = async (query: string) => {
        if (query.length < 2) return [];
        try {
            return await dispatch(
                autoAddress({ addressInput: query, interceptorOption: { skipErrorPopup: true } })
            ).unwrap();
        } catch {
            return [];
        }
    };

    const transformAddressResponse = (response: any): any[] =>
        (response?.data || []).map((item: any) => ({
            value: item.description,
            data: item.placeId,
            label: item.description,
        }));

    const handleAddressSelect = async (value: any) => {
        if (!value?.data) return;
        try {
            const response = await dispatch(
                fetchAddressDetails({ placeID: value.data, interceptorOption: { skipErrorPopup: true } })
            ).unwrap();
            if (response?.data) {
                const { streetAddress, unitNumber, city, state, zipCode } = response.data;
                setValue("newPatient.patientAddressInfo.patientStreetAddress", streetAddress);
                setValue("newPatient.patientAddressInfo.patientUnitNumber", unitNumber || "");
                setValue("newPatient.patientAddressInfo.patientCity", city);
                setValue("newPatient.patientAddressInfo.patientState", state);
                setValue("newPatient.patientAddressInfo.patientZipCode", zipCode);
                trigger(["newPatient.patientAddressInfo.patientCity", "newPatient.patientAddressInfo.patientZipCode", "newPatient.patientAddressInfo.patientState"]);
            }
        } catch {
            console.error("Error fetching address details");
        }
    };
    
    const handleBillToAddressSelect = async (value: any) => {
        if (!value?.data) return;
        try {
            const response = await dispatch(
                fetchAddressDetails({ placeID: value.data, interceptorOption: { skipErrorPopup: true } })
            ).unwrap();
            if (response?.data) {
                const { streetAddress, city, state, zipCode } = response.data;
                setValue("billToAddress.address", streetAddress);
                setValue("billToAddress.city", city || "");
                setValue("billToAddress.state", state);
                setValue("billToAddress.zip", zipCode);
                trigger(["billToAddress.city", "billToAddress.zip", "billToAddress.state"]);
            }
        } catch {
            console.error("Error fetching address details");
        }
    };

    const handleShipToAddressSelect = async (value: any) => {
        if (!value?.data) return;
        try {
            const response = await dispatch(
                fetchAddressDetails({ placeID: value.data, interceptorOption: { skipErrorPopup: true } })
            ).unwrap();
            if (response?.data) {
                const { streetAddress, city, state, zipCode } = response.data;
                setValue("shipToAddress.address", streetAddress);
                setValue("shipToAddress.city", city || "");
                setValue("shipToAddress.state", state);
                setValue("shipToAddress.zip", zipCode);
                trigger(["shipToAddress.city", "shipToAddress.zip", "shipToAddress.state"]);
            }
        } catch {
            console.error("Error fetching address details");
        }
    };

    useEffect(() => {
        if (allCodeTableValueFetchStatus === "success" && allCodeTablesValuesMap) {
            const sorted = [
                ...getCodeTableValuesByTableName(allCodeTablesValuesMap, "StateList"),
            ].sort((a, b) => a.displayValue.localeCompare(b.displayValue));
            setStateList(sorted);
        }
    }, [allCodeTablesValuesMap, allCodeTableValueFetchStatus]);

    const watchedPatientId = watch("patientId");

    useEffect(() => {
        if (!watchedPatientId || isNewPatient) {
            setSelectedPatientDetails(null);
            return;
        }
        const fetchPatientDetails = async () => {
            setLoadingPatientDetails(true);
            try {
                const result = await dispatch(getPatientWithActivePatientInfo({ patientId: watchedPatientId.toString() }));
                if (result?.payload) {
                    setSelectedPatientDetails(result.payload);
                }
            } catch (error) {
                console.error("Failed to fetch patient details:", error);
            } finally {
                setLoadingPatientDetails(false);
            }
        };
        fetchPatientDetails();
    }, [watchedPatientId, isNewPatient]);

    useEffect(() => {
        if (!watchedPatientId || isNewPatient || !patientList?.length) return;
        const patient: any = patientList.find((p: any) => p.patientId === Number(watchedPatientId));
        if (patient) {
            setValue('patientDetails.firstName', patient.firstName || '');
            setValue('patientDetails.middleName', patient.middleName || '');
            setValue('patientDetails.lastName', patient.lastName || '');
            setValue('patientDetails.birthDate', patient.birthDate || '');
            setValue('patientDetails.gender', patient.gender || '');
        }
    }, [watchedPatientId, isNewPatient, patientList]);

    const watchedProviderId = watch("providerId");

    useEffect(() => {
        if (!watchedProviderId || !practiceProvidersInfo?.length) return;
        const provider: any = practiceProvidersInfo.find((p: any) => p.providerId === Number(watchedProviderId));
        if (provider) {
            setValue('providerDetails.npi', provider.npi || '');
            setValue('providerDetails.dea', provider.dea || '');
            setValue('providerDetails.firstName', provider.firstName || '');
            setValue('providerDetails.lastName', provider.lastName || '');
        }
    }, [watchedProviderId, practiceProvidersInfo]);

    // Re-attest if provider or patient location changes after a prior confirmation.
    useEffect(() => {
        clearPrescriberConfirmation();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        watchedValues?.providerId,
        watchedValues?.shipTo,
        watchedValues?.shipToAddress?.state,
        watchedValues?.newPatient?.patientAddressInfo?.patientState,
        selectedPatientDetails,
        isNewPatient,
    ]);

    const stateListOptions = useMemo(() => {
        return stateList.map((item) => ({
            label: item.displayValue,
            value: item.keyName,
        }));
    }, [stateList]);

    const prescriberConfirmPopupCopy = useMemo(() => {
        const first = watchedValues?.providerDetails?.firstName || "";
        const last = watchedValues?.providerDetails?.lastName || "";
        const providerName = `${first} ${last}`.trim() || "Provider";
        const npi = watchedValues?.providerDetails?.npi || "—";
        const shipTo = watchedValues?.shipTo || "";
        const shipToState = watchedValues?.shipToAddress?.state || "";
        const patientState = resolvePatientState({
            shipTo,
            shipToState,
            patientResidencyState: isNewPatient
                ? watchedValues?.newPatient?.patientAddressInfo?.patientState
                : (() => {
                    if (!selectedPatientDetails) return "";
                    const responseArray = Array.isArray(selectedPatientDetails)
                        ? selectedPatientDetails
                        : [selectedPatientDetails];
                    const patientInfo = responseArray[0]?.data?.patientInfo;
                    if (!patientInfo?.length) return "";
                    const addressRecord = patientInfo.find(
                        (record: any) => record.infoType === "ADDRESS" && record.inactiveDate === null
                    );
                    return addressRecord?.patientinfoData?.patientState || "";
                })(),
        });
        const patientStateLabel =
            stateList.find((s) => String(s.keyName).toUpperCase() === patientState)?.displayValue
            || patientState
            || "the patient state";

        return {
            providerName,
            npi,
            patientState,
            patientStateLabel,
            acknowledgmentText: `I, ${providerName}, NPI ${npi}, am licensed to prescribe in ${patientStateLabel} where this patient is located, this prescription is patient-specific and medically necessary, and I am responsible for compliance with all applicable laws.`,
        };
    }, [
        watchedValues?.providerDetails?.firstName,
        watchedValues?.providerDetails?.lastName,
        watchedValues?.providerDetails?.npi,
        watchedValues?.shipTo,
        watchedValues?.shipToAddress?.state,
        watchedValues?.newPatient?.patientAddressInfo?.patientState,
        selectedPatientDetails,
        isNewPatient,
        stateList,
    ]);

    const radioSx = {
        color: tokens.textSecondary,
        "&.Mui-checked": { color: tokens.accent },
        padding: "4px 8px",
    };

    const radioLabelSx = {
        fontSize: "0.85rem",
        color: tokens.textPrimary,
    };

    const loadPatientsForPractice = async (practiceId: string) => {
        setLoadingPatients(true);
        await dispatch(getPatientListByPracticeId({ practiceId }));
        setLoadingPatients(false);
    };
    const loadPracticeProvidersForPractice = async (practiceId: string) => {
        setLoadingProviders(true);
        await dispatch(fetchActivePracticeProvidersData({ productId: practiceId.toString(), onlyActiveRecords: true }));
        setLoadingProviders(false);
    };
    const loadPracticeLocationsForPractice = async (practiceId: string) => {
        setLoadingLocations(true);
        await dispatch(fetchActivePracticeLocationsData(practiceId.toString()));
        setLoadingLocations(false);
    };

    const resetFields = () => {
        resetField('patientId');
        resetField('providerId');
        resetField('practiceLocationId');
    }   

    const handlePracticeChange = () => {
        resetFields();
        // CS-2075 — the watch(practiceId) effect already loads these on change; calling here doubled each API.
        // const practiceId = getValues('practiceId');
        // if (!!practiceId) {
        //     loadPatientsForPractice(practiceId.toString());
        //     loadPracticeProvidersForPractice(practiceId.toString());
        //     loadPracticeLocationsForPractice(practiceId.toString());
        // }
    }

    const patientOptions: Option[] = useMemo(() => {
        if (!patientList?.length) return [];
        return patientList.map((p: any) => ({
            value: p.patientId,
            label:
                fullName("", p.firstName, p.middleName, p.lastName) +
                " — DOB: " +
                p.birthDate,
        }));
    }, [patientList]);

    const practiceLocationsOptions = useMemo(() => {
        let options: { label: string; value: number }[] = [];
        practiceLocationsInfo?.map((option: any) => {
            const locationParts = [option.streetAddress, option.unitNumber, option.city, option.state, option.zipCode];
            const filteredLocationParts = locationParts.filter(part => part !== undefined && part !== null && part.trim() !== "");
            const locationName = filteredLocationParts?.join(", ")
            options.push({
                label: locationName,
                value: option.locationId,
            });
        });
        return options;
    }, [practiceLocationsInfo]);

    // Single active practice location — default the dropdown.
    useEffect(() => {
        if (loadingLocations) return;
        const practiceId = getValues("practiceId");
        if (!practiceId || practiceLocationsOptions.length !== 1) return;

        const soleLocationId = practiceLocationsOptions[0].value;
        const currentLocationId = getValues("practiceLocationId");
        if (currentLocationId && Number(currentLocationId) === Number(soleLocationId)) return;

        setValue("practiceLocationId", soleLocationId);
    }, [
        loadingLocations,
        practiceLocationsOptions,
        watchedValues.practiceId,
        setValue,
        getValues,
    ]);

    const handlePatientDateChange = () => {
        const birthDate = !!getValues('newPatient.birthDate') ? moment(getValues('newPatient.birthDate')?.toString()).format("MM/DD/YYYY") : '';
        setValue('newPatient.birthDate', birthDate);
    }

    const renderStep1 = () => {
        const showStep1AgreementPrice =
            !!watchedValues?.practiceId &&
            (useExecutedAgreementPracticeRules
                ? shouldFilterPracticesByExecutedAgreement
                : (fromExploreMarket || lockPracticeSelection) && isGenesisPharmacy);
        const genesisExplorePriceLabel = showStep1AgreementPrice
                ? (() => {
                    const unit =
                        agreementUnitExtendedPrice ??
                        singleSkuUnitExtendedPrice(agreementSkuPrices) ?? // CS-2075
                        parseAgreementUnitExtendedPrice(product.extendedPrice);
                    return unit != null ? priceFormatter(unit) : null;
                })()
                : null;

        return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>

            {/* Product Banner */}
            <Box sx={{
                borderRadius: "10px",
                border: `1px solid ${ACCENT_BORDER}`,      
                backgroundColor: ACCENT_LIGHT,             
                p: 1.8,
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 2,
            }}>
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, minWidth: 0, flex: 1 }}>
                    <Box sx={{
                        width: 38, height: 38, borderRadius: "8px",
                        backgroundColor: ACCENT_LIGHT,           
                        border: `1px solid ${ACCENT_BORDER}`,    
                        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                    }}>
                        <DescriptionOutlinedIcon sx={{ fontSize: "1.1rem", color: tokens.accent }} />
                    </Box>
                    <Box>
                        <Typography sx={{ fontSize: "0.875rem", fontWeight: 700, color: tokens.textPrimary }}>
                            {product.productName}
                        </Typography>
                        <Typography sx={{ fontSize: "0.75rem", color: tokens.textSecondary, mt: 0.2 }}>
                            {product.manufacturerName}
                        </Typography>
                        <Chip
                            label={product.productCategory}
                            size="small"
                            sx={{
                                mt: 0.8, height: "20px", fontSize: "0.68rem", fontWeight: 600,
                                backgroundColor: ACCENT_LIGHT,       
                                color: tokens.accent,
                                border: `1px solid ${ACCENT_BORDER}`, 
                                borderRadius: "5px",
                            }}
                        />
                    </Box>
                </Box>
                {genesisExplorePriceLabel && (
                    <Box sx={{ textAlign: "right", flexShrink: 0, alignSelf: "flex-start" }}>
                        <Typography sx={{
                            fontSize: "0.62rem", fontWeight: 700, color: tokens.textSecondary,
                            textTransform: "uppercase", letterSpacing: "0.06em", lineHeight: 1.2,
                        }}>
                            Price
                        </Typography>
                        <Typography sx={{
                            fontSize: "1.05rem", fontWeight: 800, color: tokens.textPrimary,
                            lineHeight: 1.25, mt: 0.35, whiteSpace: "nowrap",
                        }}>
                            {genesisExplorePriceLabel}
                        </Typography>
                    </Box>
                )}
            </Box>


            <Box>
                <Box sx={{ mb: 2 }}>
                    <FieldLabel label="Practice Name" required />
                    <Box sx={dropdownWrapSx(!!errors.practiceId)}>
                        <CustomDropdownField
                            control={control}
                            options={practiceOptions}
                            label=""
                            name="practiceId"
                            rules={{ required: "Practice Name is required." }}
                            disabled={
                                practiceOptions?.length === 1 ||
                                lockPracticeSelection ||
                                agreementPracticesLoading
                            }
                            onChangeSelect={handlePracticeChange}
                            size="small"
                            placeholderText={
                                agreementPracticesLoading
                                    ? "Loading practices..."
                                    : shouldFilterPracticesByExecutedAgreement &&
                                      !agreementPracticesLoading &&
                                      practiceOptions.length === 0
                                        ? useExecutedAgreementPracticeRules
                                            ? "No practice with executed product agreement"
                                            : "No practice with agreement for this product"
                                        : "Select a practice..."
                            }
                            sortOptions={false}
                        />
                    </Box>
                </Box>

                <Box sx={{ mb: 2 }}>
                    <FieldLabel label="Select Patient" required />
                    {!isNewPatient ? (
                        <Box sx={dropdownWrapSx(!!errors.patientId)}>
                            <CustomDropdownField
                                control={control}
                                options={patientOptions}
                                label=""
                                name="patientId"
                                rules={{ required: "Patient is required." }}
                                size="small"
                                placeholderText={
                                    !getValues('practiceId')
                                        ? "Select a practice first..."
                                        : loadingPatients
                                            ? "Loading patients..."
                                            : patientOptions.length === 0
                                                ? "No active patients found..."
                                                : "Select a patient..."
                                }
                                sortOptions={false}
                                disabled={!getValues('practiceId')}
                            />
                            <Button
                                size="small"
                                startIcon={<AddIcon sx={{ fontSize: "0.9rem !important", marginTop: 1 }} />}
                                onClick={() => { setIsNewPatient(true); resetField('patientId') }}
                                sx={{
                                    alignSelf: "flex-start",
                                    fontSize: "0.775rem", fontWeight: 600,
                                    color: tokens.accent,
                                    border: `1px solid ${tokens.border}`,
                                    borderRadius: "8px", px: 1.5, py: 0.6,
                                    textTransform: "none",
                                    "&:hover": { backgroundColor: ACCENT_LIGHT },
                                    mt: 1
                                }}
                            >
                                Add New Patient
                            </Button>
                        </Box>
                    ) : (
                        <Box sx={{
                            border: `1px solid ${tokens.border}`,
                            borderRadius: "10px",
                            p: 2,
                            backgroundColor: tokens.inputBg,
                        }}>
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
                                <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: tokens.textPrimary }}>
                                    New Patient
                                </Typography>
                                <Button
                                    size="small"
                                    onClick={() => setIsNewPatient(false)}
                                    sx={{ fontSize: "0.75rem", color: tokens.textSecondary, textTransform: "none", minWidth: "auto", p: 0 }}
                                >
                                    Cancel
                                </Button>
                            </Box>

                            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                                <Box>
                                    <FieldLabel label="First Name" required />
                                    <CustomTextField
                                        control={control}
                                        controllerName="newPatient.firstName"
                                        label=""
                                        size="small"
                                        placeholder="Enter first name"
                                        rules={{ required: "First Name is required." }}
                                        sx={inputFieldSx()}
                                    />
                                </Box>
                                <Box>
                                    <FieldLabel label="Last Name" required />
                                    <CustomTextField
                                        control={control}
                                        controllerName="newPatient.lastName"
                                        label=""
                                        size="small"
                                        placeholder="Enter last name"
                                        rules={{ required: "Last Name is required." }}
                                        sx={inputFieldSx()}
                                    />
                                </Box>
                                <Box>
                                    <FieldLabel label="Date of Birth" required />
                                    <Box sx={datepickerWrapSx()}>
                                        <CustomDatePicker
                                            label=""
                                            control={control}
                                            controllerName="newPatient.birthDate"
                                            rules={{ required: "Date Of Birth is required." }}
                                            size="small"
                                            onChange={handlePatientDateChange}
                                            isDisableFuture
                                        />
                                    </Box>
                                </Box>
                                <Box>
                                    <FieldLabel label="Gender" required />
                                    <Box sx={dropdownWrapSx()}>
                                        <CustomDropdownField
                                            control={control}
                                            options={[
                                                { label: "Male", value: "Male" },
                                                { label: "Female", value: "Female" },
                                            ]}
                                            label=""
                                            name="newPatient.gender"
                                            rules={{ required: "Gender is required." }}
                                            size="small"
                                            placeholderText="Select the gender..."
                                            sortOptions={false}
                                        />
                                    </Box>
                                </Box>
                                <Box>
                                    <FieldLabel label="Phone" required />
                                    <Box sx={inputFieldSx()}>
                                        <CustomPatternTextField
                                            control={control}
                                            name="newPatient.phone"
                                            label="Phone"
                                            size="small"
                                            rules={newPatientPhoneRules}
                                        />
                                    </Box>
                                </Box>

                            </Box>

                            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mt: 2 }}>
                                <Box>
                                    <FieldLabel label="Street Address" required />
                                    <Box sx={inputFieldSx(!!stepValidationAttempted[0] && !watchedValues?.newPatient?.patientAddressInfo?.patientStreetAddress)} data-validation-error={stepValidationAttempted[0] && !watchedValues?.newPatient?.patientAddressInfo?.patientStreetAddress ? "true" : undefined}>
                                        <CustomAutocompleteField
                                            name="newPatient.patientAddressInfo.patientStreetAddress"
                                            control={control}
                                            label=""
                                            isMultiple={false}
                                            isFreeSolo={true}
                                            fetchFunction={fetchStreetSuggestions}
                                            transformResponse={transformAddressResponse}
                                            onChangeSelect={handleAddressSelect}
                                            onChange={() => trigger(["newPatient.patientAddressInfo.patientCity", "newPatient.patientAddressInfo.patientZipCode", "newPatient.patientAddressInfo.patientState"])}
                                            size="small"
                                            placeholderText="123 Main St"
                                        />
                                    </Box>
                                </Box>
                                <Box>
                                    <FieldLabel label="Unit Number" />
                                    <Box sx={inputFieldSx()}>
                                        <CustomTextField
                                            controllerName="newPatient.patientAddressInfo.patientUnitNumber"
                                            control={control}
                                            label=""
                                            size="small"
                                            placeholder="Suite 100"
                                        />
                                    </Box>
                                </Box>
                            </Box>

                            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 2, mt: 2 }}>
                                <Box>
                                    <FieldLabel label="City" />
                                    <Box sx={inputFieldSx(!!stepValidationAttempted[0] && !watchedValues?.newPatient?.patientAddressInfo?.patientCity)} data-validation-error={stepValidationAttempted[0] && !watchedValues?.newPatient?.patientAddressInfo?.patientCity ? "true" : undefined}>
                                        <CustomTextField
                                            controllerName="newPatient.patientAddressInfo.patientCity"
                                            control={control}
                                            label=""
                                            size="small"
                                            placeholder="City"
                                        />
                                    </Box>
                                </Box>
                                <Box>
                                    <FieldLabel label="State" />
                                    <Box sx={dropdownWrapSx(!!stepValidationAttempted[0] && !watchedValues?.newPatient?.patientAddressInfo?.patientState)} data-validation-error={stepValidationAttempted[0] && !watchedValues?.newPatient?.patientAddressInfo?.patientState ? "true" : undefined}>
                                        <CustomDropdownField
                                            name="newPatient.patientAddressInfo.patientState"
                                            control={control}
                                            label=""
                                            options={stateListOptions}
                                            size="small"
                                            placeholderText="State"
                                            sortOptions={false}
                                        />
                                    </Box>
                                </Box>
                                <Box>
                                    <FieldLabel label="Zip" />
                                    <Box sx={inputFieldSx()}>
                                        <CustomTextField
                                            controllerName="newPatient.patientAddressInfo.patientZipCode"
                                            control={control}
                                            label=""
                                            size="small"
                                            placeholder="12345"
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    )}
                </Box>

                {!isNewPatient && watchedPatientId && (
                    <Box sx={{
                        border: `1px solid ${tokens.border}`,
                        borderRadius: "10px",
                        p: 2,
                        backgroundColor: tokens.inputBg,
                        mt: 1,
                        minHeight: 200,
                    }}>
                        <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: tokens.textPrimary, mb: 1.5 }}>
                            Patient Details
                        </Typography>

                        {loadingPatientDetails ? (
                            <Box sx={{ minHeight: 140 }}>
                                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 2 }}>
                                    {[0, 1, 2].map((item) => (
                                        <Box key={`patient-details-skeleton-row-1-${item}`}>
                                            <Skeleton variant="rounded" width="45%" height={14} sx={{ mb: 0.8, borderRadius: "6px" }} />
                                            <Skeleton variant="rounded" width="100%" height={36} sx={{ borderRadius: "8px" }} />
                                        </Box>
                                    ))}
                                </Box>
                                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 2, mt: 2 }}>
                                    {[0, 1].map((item) => (
                                        <Box key={`patient-details-skeleton-row-2-${item}`}>
                                            <Skeleton variant="rounded" width="38%" height={14} sx={{ mb: 0.8, borderRadius: "6px" }} />
                                            <Skeleton variant="rounded" width="100%" height={36} sx={{ borderRadius: "8px" }} />
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        ) : (
                            <>
                                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 2 }}>
                                    <Box>
                                        <FieldLabel label="First Name" required />
                                        <CustomTextField
                                            control={control}
                                            controllerName="patientDetails.firstName"
                                            label=""
                                            size="small"
                                            placeholder="First name"
                                            rules={{ required: "First Name is required." }}
                                            sx={inputFieldSx()}
                                            isDisabled={true}
                                        />
                                    </Box>
                                    <Box>
                                        <FieldLabel label="Middle" />
                                        <CustomTextField
                                            control={control}
                                            controllerName="patientDetails.middleName"
                                            label=""
                                            size="small"
                                            placeholder="Middle name"
                                            sx={inputFieldSx()}
                                            isDisabled={true}
                                        />
                                    </Box>
                                    <Box>
                                        <FieldLabel label="Last Name" required />
                                        <CustomTextField
                                            control={control}
                                            controllerName="patientDetails.lastName"
                                            label=""
                                            size="small"
                                            placeholder="Last name"
                                            rules={{ required: "Last Name is required." }}
                                            sx={inputFieldSx()}
                                            isDisabled={true}
                                        />
                                    </Box>
                                </Box>
                                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 2, mt: 2 }}>
                                    <Box>
                                        <FieldLabel label="DOB" required />
                                        <Box sx={datepickerWrapSx()}>
                                            <CustomDatePicker
                                                label=""
                                                control={control}
                                                controllerName="patientDetails.birthDate"
                                                size="small"
                                                isDisableFuture
                                                disabled={true}
                                            />
                                        </Box>
                                    </Box>
                                    <Box>
                                        <FieldLabel label="Gender" />
                                        <Box sx={dropdownWrapSx()}>
                                            <CustomDropdownField
                                                control={control}
                                                options={[
                                                    { label: "Male", value: "Male" },
                                                    { label: "Female", value: "Female" },
                                                ]}
                                                label=""
                                                name="patientDetails.gender"
                                                size="small"
                                                placeholderText="Select gender..."
                                                sortOptions={false}
                                                disabled={true}
                                            />
                                        </Box>
                                    </Box>
                                    {isGenesisPharmacy && (
                                    <Box>
                                        <FieldLabel label="Phone" required />
                                        <Box sx={inputFieldSx()}>
                                            <CustomPatternTextField
                                                control={control}
                                                name="patientDetails.phone"
                                                label="Phone"
                                                size="small"
                                                rules={newPatientPhoneRules}
                                            />
                                        </Box>
                                    </Box>
                                    )}
                                </Box>
                            </>
                        )}
                    </Box>
                )}
            </Box>
        </Box>
        );
    };

    const renderStep2 = () => (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            <Box>
                <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: tokens.accent }}>
                    Select Provider
                </Typography>
                <Typography sx={{ fontSize: "0.78rem", color: tokens.textSecondary, mt: 0.3 }}>
                    Choose a prescribing provider; their details will appear below for review/edit.
                </Typography>
            </Box>

            <Box>
                <FieldLabel label="Provider" required />
                <Box sx={dropdownWrapSx(!!errors.providerId)}>
                    <CustomDropdownField
                        control={control}
                        options={practiceProvidersOptions}
                        label=""
                        name="providerId"
                        rules={{ required: "Provider is required." }}
                        size="small"
                        placeholderText={
                            !getValues('practiceId')
                                ? "Select a practice first..."
                                : loadingProviders
                                    ? "Loading Providers..."
                                    : practiceProvidersOptions.length === 0
                                        ? "No active Providers found..."
                                        : "Select a Provider..."
                        }
                        sortOptions={false}
                        disabled={!getValues('practiceId')}
                    />
                </Box>
            </Box>

            {watchedProviderId && (
                <Box sx={{
                    border: `1px solid ${tokens.border}`,
                    borderRadius: "10px",
                    p: 2,
                    backgroundColor: tokens.inputBg,
                }}>
                    <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: tokens.textPrimary, mb: 1.5 }}>
                        Provider Details (editable)
                    </Typography>
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                        <Box>
                            <FieldLabel label="NPI" required />
                            <CustomTextField
                                control={control}
                                controllerName="providerDetails.npi"
                                label=""
                                size="small"
                                placeholder="NPI number"
                                rules={{ required: "NPI is required." }}
                                sx={inputFieldSx()}
                            />
                        </Box>
                        {/* <Box>
                            <FieldLabel label="DEA" />
                            <CustomTextField
                                control={control}
                                controllerName="providerDetails.dea"
                                label=""
                                size="small"
                                placeholder="DEA number"
                                sx={inputFieldSx()}
                            />
                        </Box> */}
                    </Box>
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mt: 2 }}>
                        <Box>
                            <FieldLabel label="First Name" required />
                            <CustomTextField
                                control={control}
                                controllerName="providerDetails.firstName"
                                label=""
                                size="small"
                                placeholder="First name"
                                rules={{ required: "First Name is required." }}
                                sx={inputFieldSx()}
                            />
                        </Box>
                        <Box>
                            <FieldLabel label="Last Name" required />
                            <CustomTextField
                                control={control}
                                controllerName="providerDetails.lastName"
                                label=""
                                size="small"
                                placeholder="Last name"
                                rules={{ required: "Last Name is required." }}
                                sx={inputFieldSx()}
                            />
                        </Box>
                    </Box>
                </Box>
            )}

            <Box>
                <FieldLabel label="Practice Location" required />
                <Box sx={dropdownWrapSx(!!errors.practiceLocationId)}>
                    <CustomDropdownField
                        control={control}
                        options={practiceLocationsOptions}
                        label=""
                        name="practiceLocationId"
                        rules={{ required: "Practice location is required." }}
                        size="small"
                        placeholderText={
                            !getValues('practiceId')
                                ? "Select a practice first..."
                                : loadingLocations
                                    ? "Loading Practice Locations..."
                                    : practiceLocationsOptions.length === 0
                                        ? "No Practice Locations found..."
                                        : "Select a Practice Location..."
                        }
                        sortOptions={false}
                        disabled={!getValues('practiceId')}
                    />
                </Box>
            </Box>
        </Box>
    );

    // Helper to get selected location address string
    const selectedLocationAddress = useMemo(() => {
        const locationId = getValues('practiceLocationId');
        if (!locationId || !practiceLocationsInfo) return null;
        const loc = practiceLocationsInfo.find((l: any) => l.locationId === Number(locationId));
        if (!loc) return null;
        return [loc.streetAddress, loc.unitNumber, loc.city, loc.state, loc.zipCode]
            .filter(Boolean).join(", ");
    }, [watchedValues.practiceLocationId, practiceLocationsInfo]);

    const newPatientAddress = useMemo(() => {
        const addr = watchedValues?.newPatient?.patientAddressInfo;
        if (!addr) return null;
        return [addr.patientStreetAddress, addr.patientUnitNumber, addr.patientCity, addr.patientState, addr.patientZipCode]
            .filter(Boolean).join(", ");
    }, [
        watchedValues?.newPatient?.patientAddressInfo?.patientStreetAddress,
        watchedValues?.newPatient?.patientAddressInfo?.patientUnitNumber,
        watchedValues?.newPatient?.patientAddressInfo?.patientCity,
        watchedValues?.newPatient?.patientAddressInfo?.patientState,
        watchedValues?.newPatient?.patientAddressInfo?.patientZipCode,
    ]);

    const existingPatientAddress = useMemo(() => {
        if (!selectedPatientDetails) return null;

        const responseArray = Array.isArray(selectedPatientDetails)
            ? selectedPatientDetails
            : [selectedPatientDetails];

        const patientInfo = responseArray[0]?.data?.patientInfo;
        if (!patientInfo?.length) return null;

        const addressRecord = patientInfo.find((record: any) =>
            record.infoType === "ADDRESS" && record.inactiveDate === null
        );

        if (!addressRecord) return null;

        const d = addressRecord.patientinfoData;

        return [
            d.patientStreetAddress,
            d.patientUnitNumber,
            d.patientCity,
            d.patientState,
            d.patientZip,
        ].filter(Boolean).join(", ") || null;
    }, [selectedPatientDetails]);

    const patientAddress = isNewPatient ? newPatientAddress : existingPatientAddress;

    const selectedLocationData = useMemo(() => {
        const locationId = getValues('practiceLocationId');
        if (!locationId || !practiceLocationsInfo) return null;
        const loc = practiceLocationsInfo.find((l: any) => l.locationId === Number(locationId));
        if (!loc) return null;
        return { address: loc.streetAddress || '', city: loc.city || '', state: loc.state || '', zip: loc.zipCode || '' };
    }, [watchedValues.practiceLocationId, practiceLocationsInfo]);

    const patientAddressData = useMemo(() => {
        if (isNewPatient) {
            const addr = watchedValues?.newPatient?.patientAddressInfo;
            if (!addr) return null;
            return { address: addr.patientStreetAddress || '', city: addr.patientCity || '', state: addr.patientState || '', zip: addr.patientZipCode || '', phone: '' };
        }
        if (!selectedPatientDetails) return null;
        const responseArray = Array.isArray(selectedPatientDetails) ? selectedPatientDetails : [selectedPatientDetails];
        const patientInfo = responseArray[0]?.data?.patientInfo;
        if (!patientInfo?.length) return null;
        const addressRecord = patientInfo.find((record: any) => record.infoType === "ADDRESS" && record.inactiveDate === null);
        if (!addressRecord) return null;
        const d = addressRecord.patientinfoData;
        const phoneRecord = patientInfo.find((record: any) => record.infoType === "PHONE" && record.inactiveDate === null);
        return {
            address: d.patientStreetAddress || '',
            city: d.patientCity || '',
            state: d.patientState || '',
            zip: d.patientZip || '',
            phone: phoneRecord?.patientinfoData?.phone || '',
        };
    }, [isNewPatient, selectedPatientDetails, watchedValues?.newPatient?.patientAddressInfo]);

    useEffect(() => {
        const billTo = watchedValues.billTo;
        if (!billTo) return;
        if (billTo === "clinic" && selectedLocationData) {
            setValue('billToAddress.address', selectedLocationData.address);
            setValue('billToAddress.city', selectedLocationData.city);
            setValue('billToAddress.state', selectedLocationData.state);
            setValue('billToAddress.zip', selectedLocationData.zip);
            setValue('billToAddress.phone', getSelectedPractice?.contactPhone || '');
        } else if (billTo === "patient") {
            if (isNewPatient) {
                const addr = getValues('newPatient.patientAddressInfo');
                setValue('billToAddress.address', addr?.patientStreetAddress || '');
                setValue('billToAddress.city', addr?.patientCity || '');
                setValue('billToAddress.state', addr?.patientState || '');
                setValue('billToAddress.zip', addr?.patientZipCode || '');
                setValue('billToAddress.phone', getValues('newPatient.phone') || '');
            } else if (patientAddressData) {
                setValue('billToAddress.address', patientAddressData.address);
                setValue('billToAddress.city', patientAddressData.city);
                setValue('billToAddress.state', patientAddressData.state);
                setValue('billToAddress.zip', patientAddressData.zip);
                setValue('billToAddress.phone', getValues('patientDetails.phone') || patientAddressData.phone || ''); // CS-2075
            }
        }
    }, [watchedValues.billTo, selectedLocationData, patientAddressData, isNewPatient, getSelectedPractice]);

    useEffect(() => {
        const shipTo = watchedValues.shipTo;
        if (!shipTo) return;
        if (shipTo === "clinic" && selectedLocationData) {
            setValue('shipToAddress.address', selectedLocationData.address);
            setValue('shipToAddress.city', selectedLocationData.city);
            setValue('shipToAddress.state', selectedLocationData.state);
            setValue('shipToAddress.zip', selectedLocationData.zip);
            setValue('shipToAddress.phone', getSelectedPractice?.contactPhone || '');
        } else if (shipTo === "patient") {
            if (isNewPatient) {
                const addr = getValues('newPatient.patientAddressInfo');
                setValue('shipToAddress.address', addr?.patientStreetAddress || '');
                setValue('shipToAddress.city', addr?.patientCity || '');
                setValue('shipToAddress.state', addr?.patientState || '');
                setValue('shipToAddress.zip', addr?.patientZipCode || '');
                setValue('shipToAddress.phone', getValues('newPatient.phone') || '');
            } else if (patientAddressData) {
                setValue('shipToAddress.address', patientAddressData.address);
                setValue('shipToAddress.city', patientAddressData.city);
                setValue('shipToAddress.state', patientAddressData.state);
                setValue('shipToAddress.zip', patientAddressData.zip);
                setValue('shipToAddress.phone', getValues('patientDetails.phone') || patientAddressData.phone || ''); // CS-2075
            }
        }
    }, [watchedValues.shipTo, selectedLocationData, patientAddressData, isNewPatient, getSelectedPractice]);

    const renderStep3 = () => (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {isGenesisPharmacy && (
                <Box
                    data-testid="genesis-first-order-billing-note"
                    sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1,
                        px: 1.5,
                        py: 1.1,
                        borderRadius: "12px",
                        border: "2px solid #f59e0b",
                        backgroundColor: isDark ? "rgba(245,158,11,0.06)" : "rgba(245,158,11,0.08)",
                        color: "#f59e0b",
                        boxShadow: "0 0 0 3px rgba(245,158,11,0.15)",
                    }}
                >
                    <Box sx={{ position: "relative", display: "flex", alignItems: "center", mt: "1px" }}>
                        <InfoOutlinedIcon sx={{ fontSize: "1.1rem", color: "#f59e0b" }} />
                    </Box>
                    <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: "#f59e0b", lineHeight: 1.55 }}>
                        {`If this is your first Rx request, ${pharmacyDisplayName} will contact your practice directly to obtain credit card authorization and payment instructions before validating and processing your request.`}
                    </Typography>
                </Box>
            )}
            <Box>
                <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: tokens.accent }}>
                    Bill To & Ship To
                </Typography>
                <Typography sx={{ fontSize: "0.78rem", color: tokens.textSecondary, mt: 0.3 }}>
                    Choose recipients and review/edit their addresses.
                </Typography>
            </Box>

            {/* Bill To */}
            <Box sx={{
                border: `1px solid ${tokens.border}`,
                borderRadius: "10px",
                p: 2,
                backgroundColor: tokens.inputBg,
            }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                    <DescriptionOutlinedIcon sx={{ fontSize: "1rem", color: tokens.accent }} />
                    <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: tokens.textPrimary }}>
                        Bill To
                        <Box component="span" sx={{ color: tokens.accent, ml: 0.3 }}>*</Box>
                    </Typography>
                </Box>
                <FormControl fullWidth>
                    <CustomRadioButton
                        control={control}
                        controllerName="billTo"
                        radioSx={radioSx}
                        radioLabelSx={radioLabelSx}
                        options={billToOptions}
                        rules={{ required: "Bill To is required." }}
                    />
                </FormControl>

                {watchedValues.billTo && (
                    <Box sx={{ mt: 1.5, display: "flex", flexDirection: "column", gap: 2 }}>
                        <Box>
                            <FieldLabel label="Address" required />
                            <Box sx={inputFieldSx()}>
                                <CustomAutocompleteField
                                    name="billToAddress.address"
                                    control={control}
                                    label=""
                                    isMultiple={false}
                                    isFreeSolo={true}
                                    fetchFunction={fetchStreetSuggestions}
                                    transformResponse={transformAddressResponse}
                                    onChangeSelect={handleBillToAddressSelect}
                                    onChange={() => trigger(["billToAddress.city", "billToAddress.zip", "billToAddress.state"])}
                                    size="small"
                                    placeholderText="123 Main St"
                                />
                            </Box>
                        </Box>
                        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 2 }}>
                            <Box>
                                <FieldLabel label="City" />
                                <CustomTextField
                                    control={control}
                                    controllerName="billToAddress.city"
                                    label=""
                                    size="small"
                                    placeholder="City"
                                    sx={inputFieldSx()}
                                />
                            </Box>
                            <Box>
                                <FieldLabel label="State" />
                                <Box sx={dropdownWrapSx()}>
                                    <CustomDropdownField
                                        name="billToAddress.state"
                                        control={control}
                                        label=""
                                        options={stateListOptions}
                                        size="small"
                                        placeholderText="State"
                                        sortOptions={false}
                                    />
                                </Box>
                            </Box>
                            <Box>
                                <FieldLabel label="ZIP" />
                                <CustomTextField
                                    control={control}
                                    controllerName="billToAddress.zip"
                                    label=""
                                    size="small"
                                    placeholder="ZIP code"
                                    sx={inputFieldSx()}
                                />
                            </Box>
                        </Box>
                        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 2 }}>
                            <Box>
                                <FieldLabel label="Phone" />
                                <Box sx={inputFieldSx()}>
                                    <CustomPatternTextField
                                        control={control}
                                        name="billToAddress.phone"
                                        label="Phone"
                                        size="small"
                                        rules={billShipPhoneRules}
                                    />
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                )}
            </Box>

            {/* Ship To */}
            <Box sx={{
                border: `1px solid ${tokens.border}`,
                borderRadius: "10px",
                p: 2,
                backgroundColor: tokens.inputBg,
            }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                    <LocalShippingOutlinedIcon sx={{ fontSize: "1rem", color: tokens.accent }} />
                    <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: tokens.textPrimary }}>
                        Ship To
                        <Box component="span" sx={{ color: tokens.accent, ml: 0.3 }}>*</Box>
                    </Typography>
                </Box>
                <FormControl fullWidth>
                    <CustomRadioButton
                        control={control}
                        controllerName="shipTo"
                        radioSx={radioSx}
                        radioLabelSx={radioLabelSx}
                        options={[
                            { value: "clinic", label: "Provider / Practice" },
                            { value: "patient", label: "Patient" },
                        ]}
                        rules={{ required: "Ship To is required." }}
                    />
                </FormControl>

                {watchedValues.shipTo && (
                    <Box sx={{ mt: 1.5, display: "flex", flexDirection: "column", gap: 2 }}>
                        <Box>
                            <FieldLabel label="Address" required />
                            <Box sx={inputFieldSx()}>
                                <CustomAutocompleteField
                                    name="shipToAddress.address"
                                    control={control}
                                    label=""
                                    isMultiple={false}
                                    isFreeSolo={true}
                                    fetchFunction={fetchStreetSuggestions}
                                    transformResponse={transformAddressResponse}
                                    onChangeSelect={handleShipToAddressSelect}
                                    onChange={() => trigger(["shipToAddress.city", "shipToAddress.zip", "shipToAddress.state"])}
                                    size="small"
                                    placeholderText="123 Main St"
                                />
                            </Box>
                        </Box>
                        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 2 }}>
                            <Box>
                                <FieldLabel label="City" />
                                <CustomTextField
                                    control={control}
                                    controllerName="shipToAddress.city"
                                    label=""
                                    size="small"
                                    placeholder="City"
                                    sx={inputFieldSx()}
                                />
                            </Box>
                            <Box>
                                <FieldLabel label="State" />
                                <Box sx={dropdownWrapSx()}>
                                    <CustomDropdownField
                                        name="shipToAddress.state"
                                        control={control}
                                        label=""
                                        options={stateListOptions}
                                        size="small"
                                        placeholderText="State"
                                        sortOptions={false}
                                    />
                                </Box>
                            </Box>
                            <Box>
                                <FieldLabel label="ZIP" />
                                <CustomTextField
                                    control={control}
                                    controllerName="shipToAddress.zip"
                                    label=""
                                    size="small"
                                    placeholder="ZIP code"
                                    sx={inputFieldSx()}
                                />
                            </Box>
                        </Box>
                        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 2 }}>
                            <Box>
                                <FieldLabel label="Phone" />
                                <Box sx={inputFieldSx()}>
                                    <CustomPatternTextField
                                        control={control}
                                        name="shipToAddress.phone"
                                        label="Phone"
                                        size="small"
                                        rules={billShipPhoneRules}
                                    />
                                </Box>
                            </Box>
                        </Box>

                        {shipToPharmacyFailures.length > 0 && (
                            <Box
                                data-validation-error="true"
                                sx={{
                                    borderRadius: "10px",
                                    border: "1px solid rgba(220,38,38,0.40)",
                                    borderLeft: "4px solid #dc2626",
                                    backgroundColor: isDark ? "rgba(220,38,38,0.10)" : "rgba(220,38,38,0.06)",
                                    px: 2,
                                    py: 1.5,
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 1,
                                }}
                            >
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <WarningIcon sx={{ fontSize: "1.05rem", color: "#dc2626" }} />
                                    <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: tokens.textPrimary }}>
                                        Cannot ship to this state
                                    </Typography>
                                </Box>
                                {shipToPharmacyFailures.map((failure) => (
                                    <Typography
                                        key={failure.code}
                                        sx={{ fontSize: "0.8rem", color: tokens.textSecondary, lineHeight: 1.55, pl: 0.3 }}
                                    >
                                        {failure.message}
                                    </Typography>
                                ))}
                            </Box>
                        )}
                    </Box>
                )}
            </Box>
        </Box>
    );

    // CS-2075 — SKU match shared by the product's SKU list and the agreement row's SKU list.
    // ponytail: ExploreMarketSKUPriceDTO has no productItemId, so only single-SKU lists resolve today;
    // add productItemId to that DTO when a multi-SKU Rx agreement has different prices per size.
    const resolveSkuUnitExtendedPrice = (skuList: any[], dosage: ProductDosageInfo): number | null => {
        const skus = skuList as Array<{
            productItemId?: number;
            extendedPrice?: unknown;
        }>;
        const skuByItem = skus.find(
            (entry) =>
                entry.productItemId != null &&
                Number(entry.productItemId) === Number(dosage.productItemId),
        );
        const fromSkuByItem = parseAgreementUnitExtendedPrice(skuByItem?.extendedPrice);
        if (fromSkuByItem != null) return fromSkuByItem;

        if (skus.length === 1) {
            const fromSingleSku = parseAgreementUnitExtendedPrice(skus[0]?.extendedPrice);
            if (fromSingleSku != null) return fromSingleSku;
        }
        return null;
    };

    const resolveDosageUnitExtendedPrice = (dosage: ProductDosageInfo): number | null => {
        if (product.hasSKUPrice && product.skuAllowablePrices?.length) {
            const fromProductSku = resolveSkuUnitExtendedPrice(product.skuAllowablePrices, dosage);
            if (fromProductSku != null) return fromProductSku;
        }
        // CS-2075 — callers without product SKU data (Patient List, Rx Request) use the agreement row's SKUs.
        if (agreementSkuPrices?.length) {
            const fromAgreementSku = resolveSkuUnitExtendedPrice(agreementSkuPrices, dosage);
            if (fromAgreementSku != null) return fromAgreementSku;
        }
        if (agreementUnitExtendedPrice != null) return agreementUnitExtendedPrice;
        return parseAgreementUnitExtendedPrice(product.extendedPrice);
    };

    const renderStep4 = () => (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }} data-prescription-drug-step>
            <Box>
                <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: tokens.accent }}>
                    Select Schedule
                </Typography>
                <Typography sx={{ fontSize: "0.78rem", color: tokens.textSecondary, mt: 0.3 }}>
                    Check one or more month schedule to include in this Rx request.
                </Typography>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {productDosageList.map((d) => {
                    const isSelected = !!selectedDosages[d.dosageId];
                    const sel = selectedDosages[d.dosageId];
                    const unitExtendedPrice = resolveDosageUnitExtendedPrice(d);
                    const scheduleLineTotalLabel =
                        isSelected && sel
                            ? formatScheduleLineTotalLabel(
                                unitExtendedPrice,
                                sel.quantity,
                            )
                            : null;
                    const showScheduleTotalPrice = isSelected && sel && scheduleLineTotalLabel;
                    return (
                        <Box
                            key={d.dosageId}
                            data-prescription-dosage-row
                            sx={{
                                borderRadius: "10px",
                                border: `1px solid ${isSelected ? tokens.accent : tokens.border}`,
                                backgroundColor: isSelected ? ACCENT_LIGHT : tokens.inputBg,  
                                p: 2,
                                transition: "all 0.15s ease",
                            }}
                        >
                            <FormControlLabel
                                sx={{
                                    alignItems: "center", m: 0, width: "100%",
                                    "& .MuiFormControlLabel-label": { flex: 1, width: "100%", minWidth: 0 },
                                }}
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={isSelected}
                                        onChange={(e) => toggleDosage(d, e.target.checked)}
                                        sx={{
                                            mr: 1, p: 0,
                                            alignSelf: "flex-start",
                                            mt: "2px",
                                            color: isDark ? "#64748b" : "#94a3b8",
                                            "&.Mui-checked": { color: "#14b8a6" },
                                        }}
                                    />
                                }
                                label={
                                    <Box sx={{ flex: 1, width: "100%" }}>
                                        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}>
                                            <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: tokens.textPrimary, flex: 1, minWidth: 0, overflowWrap: "anywhere" }}>
                                                {d.monthAndDrugName}
                                            </Typography>
                                            {showScheduleTotalPrice && (
                                                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", flexShrink: 0 }}>
                                                    <Typography sx={{
                                                        fontSize: "0.72rem", fontWeight: 700, color: tokens.textSecondary,
                                                        textTransform: "uppercase", letterSpacing: "0.05em", lineHeight: 1.2,
                                                    }}>
                                                        Total Price
                                                    </Typography>
                                                    <Typography sx={{
                                                        fontSize: "1.12rem", fontWeight: 800, color: tokens.textPrimary,
                                                        lineHeight: 1.25, mt: 0.35, whiteSpace: "nowrap",
                                                    }}>
                                                        {scheduleLineTotalLabel}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>

                                        {isSelected && (
                                            // CS-2075
                                            <Box sx={{ mt: showScheduleTotalPrice ? 0 : 1.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
                                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}>
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                        <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: tokens.textSecondary, minWidth: 28 }}>
                                                            Qty:
                                                        </Typography>
                                                        {isGenesisPharmacy ? (
                                                            /**
                                                             * Genesis fills any quantity, so the provider steps it freely
                                                             * instead of picking from the priced option list. Same stepper
                                                             * shape as the Create Order items/sizes rows.
                                                             */
                                                            <Box
                                                                data-prescription-qty-stepper
                                                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                                                sx={{
                                                                    display: "flex", alignItems: "center", gap: 0.3, p: 0.4,
                                                                    border: `1px solid ${tokens.inputBorder}`, borderRadius: "8px",
                                                                    flexShrink: 0, minWidth: 100, justifyContent: "space-between", // CS-2075
                                                                }}
                                                            >
                                                                <IconButton
                                                                    size="small"
                                                                    aria-label="Decrease quantity"
                                                                    disabled={sel.quantity <= 1}
                                                                    onClick={() => updateDosageField(d.dosageId, "quantity", Math.max(1, sel.quantity - 1))}
                                                                    sx={{ p: 0.3, color: tokens.textSecondary }}
                                                                >
                                                                    <RemoveIcon sx={{ fontSize: "0.8rem" }} />
                                                                </IconButton>
                                                                <Typography sx={{ minWidth: 22, px: 0.5, textAlign: "center", whiteSpace: "nowrap", fontSize: "0.82rem", fontWeight: 700, color: tokens.textPrimary }}>
                                                                    {sel.quantity}
                                                                </Typography>
                                                                <IconButton
                                                                    size="small"
                                                                    aria-label="Increase quantity"
                                                                    onClick={() => updateDosageField(d.dosageId, "quantity", sel.quantity + 1)}
                                                                    sx={{ p: 0.3, color: "#c4b5fd", backgroundColor: "rgba(139,92,246,.15)" }}
                                                                >
                                                                    <AddIcon sx={{ fontSize: "0.8rem" }} />
                                                                </IconButton>
                                                            </Box>
                                                        ) : d?.quantityOptions && d?.quantityOptions?.length > 0 ? (
                                                            <TextField
                                                                select size="small"
                                                                value={sel.quantity}
                                                                onChange={(e) => updateDosageField(d.dosageId, "quantity", parseInt(e.target.value))}
                                                                SelectProps={{ native: true }}
                                                                sx={{ ...inputSx(), width: "auto", minWidth: "140px", maxWidth: "220px" }}
                                                                onClick={(e) => e.stopPropagation()}
                                                            >
                                                                {d.quantityOptions.map((q) => (
                                                                    <option key={q.label} value={q.quantity}>{q.label}</option>
                                                                ))}
                                                            </TextField>
                                                        ) : (
                                                            <TextField
                                                                type="number" size="small"
                                                                value={sel.quantity}
                                                                onChange={(e) => updateDosageField(d.dosageId, "quantity", Math.max(1, parseInt(e.target.value) || 1))}
                                                                inputProps={{ min: 1 }}
                                                                sx={{ ...inputSx(), width: "90px" }}
                                                                onClick={(e) => e.stopPropagation()}
                                                            />
                                                        )}
                                                    </Box>
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                        <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: tokens.textSecondary }}>
                                                            Refill:
                                                        </Typography>
                                                        {/* CS-2075 */}
                                                        <Box
                                                            data-prescription-refill-stepper
                                                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                                            sx={{
                                                                display: "flex", alignItems: "center", gap: 0.3, p: 0.4,
                                                                border: `1px solid ${tokens.inputBorder}`, borderRadius: "8px",
                                                                flexShrink: 0, minWidth: 100, justifyContent: "space-between",
                                                            }}
                                                        >
                                                            <IconButton
                                                                size="small"
                                                                aria-label="Decrease refills"
                                                                disabled={(sel.refills ?? 0) <= 0}
                                                                onClick={() => updateDosageField(d.dosageId, "refills", Math.max(0, (sel.refills ?? 0) - 1))}
                                                                sx={{ p: 0.3, color: tokens.textSecondary }}
                                                            >
                                                                <RemoveIcon sx={{ fontSize: "0.8rem" }} />
                                                            </IconButton>
                                                            <Typography sx={{ minWidth: 22, px: 0.5, textAlign: "center", whiteSpace: "nowrap", fontSize: "0.82rem", fontWeight: 700, color: tokens.textPrimary }}>
                                                                {sel.refills ?? 0}
                                                            </Typography>
                                                            <IconButton
                                                                size="small"
                                                                aria-label="Increase refills"
                                                                onClick={() => updateDosageField(d.dosageId, "refills", (sel.refills ?? 0) + 1)}
                                                                sx={{ p: 0.3, color: "#c4b5fd", backgroundColor: "rgba(139,92,246,.15)" }}
                                                            >
                                                                <AddIcon sx={{ fontSize: "0.8rem" }} />
                                                            </IconButton>
                                                        </Box>
                                                    </Box>
                                                </Box>

                                                <Box sx={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: 1, alignItems: "flex-start" }}>
                                                    <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: tokens.textSecondary, pt: 0.8 }}>
                                                        Sig:
                                                    </Typography>
                                                    <Box onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                                                        {editingSigs[d.dosageId] ? (
                                                            <Box sx={{ position: "relative" }}>
                                                                <TextField
                                                                    fullWidth multiline rows={2} size="small"
                                                                    value={sel.sig}
                                                                    autoFocus
                                                                    onChange={(e) => updateDosageField(d.dosageId, "sig", e.target.value)}
                                                                    sx={inputSx()}
                                                                />
                                                                <Button
                                                                    size="small"
                                                                    startIcon={<SaveOutlinedIcon sx={{ fontSize: "0.8rem !important" }} />}
                                                                    onClick={() => setEditingSigs((p) => ({ ...p, [d.dosageId]: false }))}
                                                                    sx={{
                                                                        position: "absolute", top: 6, right: 6,
                                                                        fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase",
                                                                        color: tokens.accent, minWidth: "auto", p: "2px 6px",
                                                                        "&:hover": { backgroundColor: ACCENT_LIGHT }, 
                                                                    }}
                                                                >
                                                                    Save
                                                                </Button>
                                                            </Box>
                                                        ) : (
                                                            <Box sx={{ position: "relative" }}>
                                                                <Box sx={{
                                                                    border: `1px solid ${tokens.border}`,
                                                                    borderRadius: "8px",
                                                                    backgroundColor: "rgba(255,255,255,0.06)",
                                                                    px: 1.5, py: 1, pr: 7, minHeight: 40,
                                                                }}>
                                                                    <Typography sx={{ fontSize: "0.78rem", color: tokens.textPrimary, whiteSpace: "pre-wrap" }}>
                                                                        {sel.sig}
                                                                    </Typography>
                                                                </Box>
                                                                <Button
                                                                    size="small"
                                                                    startIcon={<EditOutlinedIcon sx={{ fontSize: "0.8rem !important" }} />}
                                                                    onClick={() => setEditingSigs((p) => ({ ...p, [d.dosageId]: true }))}
                                                                    sx={{
                                                                        position: "absolute", top: 6, right: 6,
                                                                        fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase",
                                                                        color: tokens.textSecondary,
                                                                        minWidth: "auto", p: "2px 6px",
                                                                        "&:hover": { color: tokens.accent, backgroundColor: ACCENT_LIGHT },
                                                                    }}
                                                                >
                                                                    Edit
                                                                </Button>
                                                            </Box>
                                                        )}
                                                        {d.dosageNote && (
                                                            <Typography sx={{
                                                                mt: 0.5, fontSize: "0.72rem",
                                                                color: tokens.textSecondary,
                                                                fontStyle: "italic",
                                                            }}>
                                                                {d.dosageNote}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Box>
                                            </Box>
                                        )}
                                    </Box>
                                }
                            />
                        </Box>
                    );
                })}
            </Box>

            {!!stepValidationAttempted[3] && Object.keys(selectedDosages).length === 0 && (
                <Typography data-validation-error="true" sx={{ color: tokens.error, fontSize: "0.75rem", fontWeight: 500 }}>
                    Please select at least one dosage.
                </Typography>
            )}

        </Box>
    );

    const renderStep5 = () => (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            <Box sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 2,
                p: 2,
                borderRadius: "10px",
                border: `1px solid ${isDark ? "rgba(20,184,166,0.28)" : "rgba(20,184,166,0.4)"}`,
                backgroundColor: isDark ? "rgba(20,184,166,0.06)" : "rgba(20,184,166,0.05)",
                position: "relative",
            }}>
                <Box sx={{
                    width: 40, height: 40, borderRadius: "8px",
                    backgroundColor: isDark ? "rgba(20,184,166,0.18)" : "rgba(20,184,166,0.14)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0, mt: 0.25,
                }}>
                    <InfoOutlinedIcon sx={{ color: "#14b8a6", fontSize: "1.3rem" }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0, pr: 3 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: tokens.textPrimary, mb: 0.5 }}>
                        Compounded Medication Notice
                    </Typography>
                    <Typography sx={{ fontSize: "0.85rem", color: tokens.textSecondary, lineHeight: 1.65 }}>
                        {`The medications available through this integration are compounded by ${pharmacyDisplayName}, a licensed 503A compounding pharmacy. Compounded medications are not FDA-approved. They are prepared for an identified individual patient based on a valid prescription from a licensed provider.`}
                    </Typography>
                </Box>
            </Box>

            <Box>
                <FieldLabel label="Allergies" required />
                <TextField
                    fullWidth multiline rows={2} size="small"
                    value={allergies}
                    onChange={(e) => setAllergies(e.target.value)}
                    placeholder="List any patient allergies, or type 'None known'…"
                    error={!!stepValidationAttempted[4] && allergies.trim().length === 0}
                    helperText={stepValidationAttempted[4] && allergies.trim().length === 0 ? "Allergies information is required." : ""}
                    sx={inputSx(!!stepValidationAttempted[4] && allergies.trim().length === 0)}
                />
            </Box>

            <Box>
                <FieldLabel label="Active Medication" />
                <TextField
                    fullWidth
                    multiline
                    rows={2}
                    size="small"
                    data-testid="txt-medicationNote"
                    value={medicationNote}
                    onChange={(e) => setMedicationNote(e.target.value)}
                    placeholder="Patient's Current Medication List"
                    sx={inputSx(false)}
                />
            </Box>
        </Box>
    );

    
    const renderSuccess = () => {
        const firstRx = submittedPrescriptions[0];

        if (submitting || submittedPrescriptions.length === 0) {
            return <PrescriptionLoadingSkeleton tokens={tokens} pharmacyName={pharmacyDisplayName} />;
        }

        // CS-2075
        const shipToAddress = getValues("shipToAddress");
        const locationStr = shipToAddress?.address
            ? {
                primary: shipToAddress.address,
                secondary: [shipToAddress.city, shipToAddress.state, shipToAddress.zip].filter(Boolean).join(", "),
            }
            : firstRx?.location
            ? {
                primary: firstRx.location.streetAddress ?? "",
                secondary: [firstRx.location.city, firstRx.location.state, firstRx.location.zip].filter(Boolean).join(", "),
            }
            : null;

        let successScheduleTotal: number | null = null;
        for (const dosage of productDosageList) {
            const sel = selectedDosages[dosage.dosageId];
            if (!sel) continue;
            const lineTotal = computeScheduleLineTotal(
                resolveDosageUnitExtendedPrice(dosage),
                sel.quantity,
            );
            if (lineTotal == null) continue;
            successScheduleTotal = (successScheduleTotal ?? 0) + lineTotal;
        }
        if (successScheduleTotal != null) {
            successScheduleTotal = Math.round(successScheduleTotal * 100) / 100;
        }
        const successScheduleTotalLabel =
            successScheduleTotal != null ? priceFormatter(successScheduleTotal) : null;

        const metaItems = [
            {
                label: "Patient",
                icon: <PersonAddAlt1Icon sx={{ fontSize: "0.85rem", color: tokens.accent }} />,
                primary: firstRx?.patientName ?? "Patient",
                secondary: product.productName,
            },
            {
                label: "Provider",
                icon: <DescriptionOutlinedIcon sx={{ fontSize: "0.85rem", color: tokens.accent }} />,
                primary: firstRx?.providerName ?? "-",
                secondary: firstRx?.providerNpi ? `NPI: ${firstRx.providerNpi}` : null,
            },
            {
                label: "Tracking",
                icon: <Typography sx={{ fontSize: "0.75rem", fontWeight: 800, color: tokens.accent }}>#</Typography>,
                primary: `${submittedPrescriptions.length}/${submittedPrescriptions.length}`,
                secondary: "Prescriptions requested",
            },
            ...(locationStr ? [{
                label: "Location",
                icon: <LocalShippingOutlinedIcon sx={{ fontSize: "0.85rem", color: tokens.accent }} />,
                primary: locationStr.primary,
                secondary: locationStr.secondary,
            }] : []),
        ];

        return (
            <>
                {showPDF.isShow && (
                    <PDFViewer
                        isOpen={showPDF.isShow}
                        onClose={() => setShowPDF({ isShow: false, title: "", fileName: "" })}
                        title={showPDF.title}
                        fileName={showPDF.fileName}
                        base64String={pdfContent}
                    />
                )}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
                    {/* Body */}
                    <Box sx={{ px: 3, py: 2.5, display: "flex", flexDirection: "column", gap: 2.5, overflowY: "auto" }}>
                        {/* Sub-header card */}
                        <Box sx={{
                            border: `1px solid ${ACCENT_BORDER}`,
                            borderRadius: "12px",
                            backgroundColor: isDark ? "rgba(20,184,166,0.08)" : "rgba(20,184,166,0.04)",
                            px: 2.5, py: 2,
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", sm: "minmax(0, 1fr) auto auto" },
                            columnGap: { xs: 2, sm: 4 },
                            rowGap: 2,
                            alignItems: "center",
                        }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 0 }}>
                                <Box sx={{
                                    width: 44, height: 44, borderRadius: "12px",
                                    background: "linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    boxShadow: "0 4px 14px rgba(20,184,166,0.35)",
                                    flexShrink: 0,
                                }}>
                                    <Typography sx={{ fontSize: "0.9rem", fontWeight: 800, color: "#fff", letterSpacing: "-0.02em", lineHeight: 1 }}>
                                        Rx
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography sx={{ fontSize: "1rem", fontWeight: 800, color: tokens.textPrimary, lineHeight: 1.2 }}>
                                        {`${pharmacyDisplayName} — prescription`}
                                    </Typography>
                                    <Typography sx={{ fontSize: "0.76rem", color: tokens.textSecondary, mt: 0.3 }}>
                                        {submittedPrescriptions.length} prescription{submittedPrescriptions.length !== 1 ? "s" : ""} requested successfully for{" "}
                                        <Box component="span" sx={{ fontWeight: 700, color: tokens.accent }}>
                                            {firstRx?.patientName ?? "Patient"}
                                        </Box>
                                    </Typography>
                                </Box>
                            </Box>
                            {successScheduleTotalLabel && (
                                <Box sx={{ textAlign: "left", justifySelf: { xs: "start", sm: "end" }, pr: { sm: 0.5 } }}>
                                    <Typography sx={{
                                        fontSize: "0.62rem", fontWeight: 700, color: tokens.accent,
                                        textTransform: "uppercase", letterSpacing: "0.06em", lineHeight: 1.2,
                                    }}>
                                        Total Price
                                    </Typography>
                                    <Typography sx={{ fontSize: "1.05rem", fontWeight: 800, color: tokens.textPrimary, lineHeight: 1.2, whiteSpace: "nowrap" }}>
                                        {successScheduleTotalLabel}
                                    </Typography>
                                </Box>
                            )}
                            <Chip
                                icon={<CheckCircleOutlineIcon sx={{ fontSize: "0.8rem !important", color: `${SUCCESS} !important` }} />}
                                label={`${submittedPrescriptions.length} Requested`}
                                size="small"
                                sx={{
                                    fontSize: "0.72rem", fontWeight: 700,
                                    backgroundColor: SUCCESS_LIGHT,
                                    color: SUCCESS,
                                    border: `1px solid ${SUCCESS}44`,
                                    borderRadius: "8px",
                                    height: 26,
                                    justifySelf: { xs: "start", sm: "end" },
                                }}
                            />
                        </Box>
                        {/* Meta cards row */}
                        <Box sx={{ display: "grid", gridTemplateColumns: `repeat(${metaItems.length}, 1fr)`, gap: 1.5 }}>
                            {metaItems.map((item) => (
                                <Box key={item.label} sx={{
                                    px: 1.5, py: 1.2,
                                    borderRadius: "10px",
                                    border: `1px solid ${tokens.border}`,
                                    backgroundColor: isDark ? "rgba(255,255,255,0.03)" : tokens.inputBg,
                                }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.4 }}>
                                        {item.icon}
                                        <Typography sx={{
                                            fontSize: "0.62rem", fontWeight: 700, color: tokens.accent,
                                            textTransform: "uppercase", letterSpacing: "0.07em",
                                        }}>
                                            {item.label}
                                        </Typography>
                                    </Box>
                                    <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, color: tokens.textPrimary, lineHeight: 1.3 }}>
                                        {item.primary}
                                    </Typography>
                                    {item.secondary && (
                                        <Typography sx={{ fontSize: "0.7rem", color: tokens.textSecondary, lineHeight: 1.3, mt: 0.2 }}>
                                            {item.secondary}
                                        </Typography>
                                    )}
                                </Box>
                            ))}
                        </Box>

                        <Box sx={{
                            border: `1px solid ${ACCENT_BORDER}`,
                            borderRadius: "12px",
                            backgroundColor: isDark ? "rgba(20,184,166,0.06)" : "rgba(20,184,166,0.03)",
                            p: 2.5,
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                        }}>
                                <Typography sx={{ fontSize: "0.88rem", fontWeight: 700, color: tokens.textPrimary, lineHeight: 1.6 }}>
                                    {`Rx Request successfully submitted to ${pharmacyDisplayName} for processing.`}
                                </Typography>

                                {submittedPrescriptions
                                    .filter((rx: { documentId?: number | null }) => rx.documentId != null)
                                    .map((rx: any, idx: number) => (
                                    <Box
                                        key={rx.documentId ?? rx.batchId ?? idx}
                                        sx={{
                                            borderRadius: "12px",
                                            border: `1px solid ${tokens.border}`,
                                            backgroundColor: isDark ? "rgba(255,255,255,0.03)" : tokens.inputBg,
                                            px: 2, py: 1.5,
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1.5,
                                            transition: "all 0.15s ease",
                                            "&:hover": {
                                                borderColor: ACCENT_BORDER,
                                                backgroundColor: ACCENT_LIGHT,
                                            },
                                        }}
                                    >
                                        <Box sx={{
                                            width: 40, height: 40, borderRadius: "10px", flexShrink: 0,
                                            backgroundColor: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
                                            display: "flex", alignItems: "center", justifyContent: "center",
                                        }}>
                                            <DescriptionOutlinedIcon sx={{ fontSize: "1.1rem", color: tokens.accent }} />
                                        </Box>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography sx={{ fontSize: "0.88rem", fontWeight: 700, color: tokens.textPrimary }}>
                                                Prescription {idx + 1}
                                            </Typography>
                                            <Typography sx={{ fontSize: "0.72rem", color: tokens.textSecondary, mt: 0.15 }}>
                                                {rx.batchId ? `(${rx.batchId})` : ""}
                                                {rx.batchId && rx.createdAt ? " \u00B7 " : ""}
                                                {rx.createdAt
                                                    ? new Date(rx.createdAt).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
                                                    : ""}
                                            </Typography>
                                        </Box>
                                        <IconButton
                                            size="small"
                                            disabled={previewingId === rx.documentId}
                                            onClick={() =>
                                                handlePreviewPrescription(
                                                    rx.documentId,
                                                    rx.providerName ?? `Prescription ${idx + 1}`,
                                                )
                                            }
                                            aria-label="Preview prescription"
                                            sx={{
                                                flexShrink: 0,
                                                p: 0.75,
                                                color: tokens.textPrimary,
                                                border: `1px solid ${tokens.border}`,
                                                borderRadius: "8px",
                                                "&:hover": {
                                                    backgroundColor: ACCENT_LIGHT,
                                                    color: tokens.accent,
                                                    borderColor: ACCENT_BORDER,
                                                },
                                                "&.Mui-disabled": { opacity: 0.35 },
                                            }}
                                        >
                                            {previewingId === rx.documentId ? (
                                                <CircularProgress size={18} sx={{ color: tokens.accent }} />
                                            ) : (
                                                <VisibilityOutlinedIcon sx={{ fontSize: "1.1rem" }} />
                                            )}
                                        </IconButton>
                                    </Box>
                                ))}
                        </Box>

                        {/* What happens next? */}
                        <Box sx={{
                            border: `1px solid ${tokens.border}`,
                            borderRadius: "12px",
                            backgroundColor: isDark ? "rgba(255,255,255,0.02)" : tokens.inputBg,
                            p: 2.5,
                        }}>
                            <Typography sx={{ fontSize: "0.92rem", fontWeight: 700, color: tokens.textPrimary, mb: 1.5 }}>
                                What happens next?
                            </Typography>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.2 }}>
                                {[
                                    `${pharmacyDisplayName} receives your prescription request and reviews your credentials`,
                                    `${pharmacyDisplayName} validates the order and prepares the compounded medication`,
                                    "You will receive a notification here once the prescription is ready",
                                    `${pharmacyDisplayName} will provide shipping details to your designated Ship To address`,
                                ].map((text, i) => (
                                    <Box key={i} sx={{ display: "flex", alignItems: "flex-start", gap: 1.2 }}>
                                        <Box sx={{
                                            width: 24, height: 24, borderRadius: "50%", flexShrink: 0,
                                            backgroundColor: ACCENT_LIGHT,
                                            border: `1px solid ${ACCENT_BORDER}`,
                                            display: "flex", alignItems: "center", justifyContent: "center",
                                        }}>
                                            <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: tokens.accent }}>
                                                {i + 1}
                                            </Typography>
                                        </Box>
                                        <Typography sx={{ fontSize: "0.82rem", color: tokens.textSecondary, lineHeight: 1.5, pt: 0.15 }}>
                                            {text}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    </Box>

                    {/* Footer */}
                    <Box sx={{
                        px: 3, py: 2,
                        borderTop: `1px solid ${tokens.border}`,
                        display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5,
                        backgroundColor: isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.015)",
                    }}>
                        <Typography sx={{ fontSize: "0.72rem", color: tokens.textSecondary, textAlign: "center", lineHeight: 1.5 }}>
                            {`V3biomed.com platform facilitates the request being submitted to ${pharmacyDisplayName}. The prescription is requested through V3biomed.com and fulfilled by ${pharmacyDisplayName}.`}
                        </Typography>
                        <Button
                            variant="outlined"
                            onClick={handleClose}
                            sx={{
                                textTransform: "none",
                                borderRadius: "8px",
                                fontWeight: 600,
                                fontSize: "0.85rem",
                                px: 4,
                                color: "#ef4444",
                                borderColor: "#ef4444",
                                "&:hover": {
                                    borderColor: "#dc2626",
                                    backgroundColor: "rgba(239,68,68,0.08)",
                                },
                            }}
                        >
                            Close
                        </Button>
                    </Box>
                </Box>
            </>
        );
    };

    return (
        <>
        <Dialog
            open={isOpen}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            fullScreen={isMobile}
            PaperProps={{ ref: prescriptionDialogPaperRef, sx: paperSx(isMobile) }}
        >
            <Box sx={headerSx}>
                <Box sx={iconBoxSx(tokens.accent)}>
                    <DescriptionOutlinedIcon sx={{ fontSize: "1.1rem", color: tokens.accent }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Typography fontWeight={700} sx={{ fontSize: "1rem", color: tokens.textPrimary, lineHeight: 1.3 }}>
                        {submitted
                            ? product.manufacturerName
                                ? `RX Request Submitted - ${product.manufacturerName}`
                                : "RX Request Submitted"
                            : product.manufacturerName
                                ? `Create Rx Request - ${product.manufacturerName}`
                                : "Create Rx Request"}
                    </Typography>
                    <Typography sx={{ fontSize: "0.75rem", color: tokens.textSecondary, mt: 0.15 }}>
                        {product.productName}
                        {product.manufacturerName ? ` — ${product.manufacturerName}` : ""}
                    </Typography>
                </Box>
                    {step === 0 && watchedValues?.practiceId && !isPracticeRegisteredWithPharmacy && !isGenesisPharmacy && (
                        <Button
                            onClick={() => setShowBillingInfoPopup(true)}
                            sx={{
                                mr: 1.5,
                                px: 1.5,
                                py: 0.4,
                                borderRadius: "999px",
                                border: "2px solid #f59e0b",
                                backgroundColor: isDark ? "rgba(245,158,11,0.06)" : "rgba(245,158,11,0.08)",
                                color: "#f59e0b",
                                fontWeight: 700,
                                fontSize: "0.88rem",
                                textTransform: "none",
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                letterSpacing: "0.01em",
                                boxShadow: "0 0 0 3px rgba(245,158,11,0.15)",
                                "&:hover": {
                                    backgroundColor: "rgba(245,158,11,0.14)",
                                    borderColor: "#fbbf24",
                                },
                            }}
                            startIcon={
                                <Box sx={{ position: "relative", display: "flex", alignItems: "center" }}>
                                    <InfoOutlinedIcon sx={{ fontSize: "1.1rem", color: "#f59e0b" }} />
                                    <Box sx={{
                                        position: "absolute",
                                        top: -3,
                                        right: -3,
                                        width: 8,
                                        height: 8,
                                        borderRadius: "50%",
                                        backgroundColor: "#f59e0b",
                                        animation: "billing-dot-pulse 1.5s ease-in-out infinite",
                                        "@keyframes billing-dot-pulse": {
                                            "0%, 100%": { opacity: 1, transform: "scale(1)" },
                                            "50%": { opacity: 0.3, transform: "scale(0.7)" },
                                        },
                                    }} />
                                </Box>
                            }
                        >
                            Billing Info
                        </Button>
                    )}
                    <IconButton
                        onClick={handleClose}
                        size="small"
                        sx={{
                            width: 34,
                            height: 34,
                            borderRadius: "10px",   // changed from "50%" to rounded square
                            border: `2px solid ${tokens.border}`,
                            backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)",
                            color: tokens.textSecondary,
                            "&:hover": {
                                backgroundColor: isDark ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.08)",
                                borderColor: tokens.textSecondary,
                                color: tokens.textPrimary,
                            },
                        }}
                    >
                        <CloseIcon sx={{ fontSize: "0.95rem" }} />
                    </IconButton>
            </Box>

            {!submitted && (
                <Box sx={{
                    px: 3, py: 2,
                    borderBottom: `1px solid ${tokens.border}`,
                    backgroundColor: tokens.bg,
                    width: "100%",
                }}>
                    {(() => {
                        const isDark = tokens.bg === "#141720";
                        const stepIcons = [
                            <PersonOutlinedIcon />,
                            <BadgeOutlinedIcon />,
                            <LocalShippingOutlinedIcon />,
                            <CalendarMonthOutlinedIcon />,
                            <NotificationsNoneOutlinedIcon />,
                        ];
                        return (
                            <Box sx={{ position: "relative", display: "flex", alignItems: "flex-start" }}>


                                {steps.map((_, i) => {
                                    if (i === steps.length - 1) return null;
                                    const isCompleted = step > i;
                                    const isActive    = step === i + 1;
                                    return (
                                        <Box
                                            key={`line-${i}`}
                                            sx={{
                                                position:     "absolute",
                                                top:          "17px", 
                                                left:         `calc(${(i + 0.5) / steps.length * 100}% + 20px)`,
                                                right:        `calc(${(steps.length - i - 1.5) / steps.length * 100}% + 20px)`,
                                                height:       "2px",
                                                borderRadius: "2px",
                                                background:   isCompleted
                                                    ? `linear-gradient(90deg, ${SUCCESS}, ${isActive ? tokens.accent : SUCCESS})`
                                                    : isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0",
                                                transition:   "background 0.3s ease",
                                                zIndex:       0,
                                            }}
                                        />
                                    );
                                })}

                                {steps.map((label, i) => {
                                    const isActive    = step === i;
                                    const isCompleted = step > i;
                                    const isPending   = step < i;

                                    const bubbleBg = isActive
                                        ? tokens.accent
                                        : isCompleted
                                            ? SUCCESS
                                            : isDark ? "rgba(255,255,255,0.07)" : "#e2e8f0";

                                    const bubbleBorder = isActive
                                        ? tokens.accent
                                        : isCompleted
                                            ? SUCCESS
                                            : isDark ? "rgba(255,255,255,0.12)" : "#cbd5e1";

                                    const labelColor = isActive
                                        ? tokens.textPrimary
                                        : isCompleted
                                            ? SUCCESS
                                            : tokens.textSecondary;

                                    const iconColor = isActive || isCompleted ? "#ffffff" : tokens.textSecondary;

                                    return (
                                        <Box
                                            key={label}
                                            sx={{
                                                flex:          1,
                                                display:       "flex",
                                                flexDirection: "column",
                                                alignItems:    "center",
                                                gap:           "6px",
                                                position:      "relative",
                                                zIndex:        1,
                                            }}
                                        >
                                            <Box sx={{
                                                width:           "34px",
                                                height:          "34px",
                                                borderRadius:    "50%",
                                                backgroundColor: bubbleBg,
                                                border:          `2px solid ${bubbleBorder}`,
                                                display:         "flex",
                                                alignItems:      "center",
                                                justifyContent:  "center",
                                                flexShrink:      0,
                                                transition:      "background-color 0.25s, border-color 0.25s",
                                                outline:         isActive
                                                    ? "3px solid rgba(20,184,166,0.22)"
                                                    : isCompleted
                                                        ? "3px solid rgba(22,163,74,0.15)"
                                                        : "3px solid transparent",
                                                outlineOffset:   "1px",
                                                "& svg": { fontSize: "0.95rem", color: iconColor },
                                            }}>
                                                {isCompleted
                                                    ? <CheckRoundedIcon sx={{ fontSize: "1rem !important", color: "#fff" }} />
                                                    : React.cloneElement(stepIcons[i], { sx: { fontSize: "0.95rem", color: iconColor } } as any)
                                                }
                                            </Box>

                                            <Typography sx={{
                                                fontSize:   "0.72rem",
                                                fontWeight: isActive ? 700 : isCompleted ? 600 : 400,
                                                color:      labelColor,
                                                whiteSpace: "nowrap",
                                                lineHeight: 1.2,
                                                transition: "color 0.2s",
                                                textAlign:  "center",
                                            }}>
                                                {label}
                                            </Typography>

                                            {/* <Typography sx={{
                                                fontSize:   "0.62rem",
                                                color:      isPending ? tokens.textSecondary : "transparent",
                                                lineHeight: 1,
                                                textAlign:  "center",
                                            }}>
                                                Step {i + 1}
                                            </Typography> */}
                                        </Box>
                                    );
                                })}
                            </Box>
                        );
                    })()}
                </Box>
            )}

            <Box sx={{
                ...(submitted ? { p: 0, overflow: "auto" } : bodySx),
                "&::-webkit-scrollbar": { width: "5px" },
                "&::-webkit-scrollbar-track": { background: "transparent" },
                "&::-webkit-scrollbar-thumb": { background: tokens.border, borderRadius: "4px" },
            }}>
                {submitted ? renderSuccess() : step === 0 ? renderStep1() : step === 1 ? renderStep2() : step === 2 ? renderStep3() : step === 3 ? renderStep4() : renderStep5()}
            </Box>

            {!submitted && (
                <Box sx={{ ...footerSx, justifyContent: "space-between", display: "flex" }}>
                    <Button
                        variant="outlined"
                        startIcon={
                            step === 0
                                ? <CancelIcon sx={{ fontSize: "1rem !important" }} />
                                : <ChevronLeftIcon sx={{ fontSize: "1rem !important" }} />
                        }
                        onClick={() => (step > 0 ? handleBack() : handleClose())}
                        sx={cancelBtnSx}
                    >
                        {step === 0 ? "Cancel" : "Back"}
                    </Button>

                    {step < steps.length - 1 ? (
                        <Button
                            variant="contained"
                            endIcon={<ChevronRightIcon sx={{ fontSize: "1rem !important" }} />}
                            onClick={handleNextStep}
                            sx={{ ...primaryBtnSx, ml: "auto" }}
                        >
                            {step === 0 ? "Continue" : step === 1 ? "Continue" : step === 2 ? "Continue" : step === 3 ? "Continue" : ""}
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            disabled={submitting}
                            startIcon={submitting
                                ? <CircularProgress size={14} sx={{ color: "inherit" }} />
                                : <ClipboardList size={18} />
                            }
                            onClick={handleSubmitClick}
                            sx={primaryBtnSx}
                        >
                            {submitting ? "Submitting..." : "Submit Rx Request"}
                        </Button>
                    )}
                </Box>
            )}
        </Dialog>

            <Dialog
                open={showGatingBlockPopup}
                onClose={() => setShowGatingBlockPopup(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        ...paperSx(false),
                        p: 0,
                        borderRadius: "14px",
                        width: "620px",
                        maxWidth: "620px",
                    },
                }}
            >
                <Box sx={{
                    display: "flex", alignItems: "center", gap: 1.5,
                    px: 2.5, py: 1.5,
                    borderBottom: `1px solid ${tokens.border}`,
                }}>
                    <Box sx={{
                        width: 40, height: 40, borderRadius: "10px", flexShrink: 0,
                        backgroundColor: "rgba(220,38,38,0.14)",
                        border: "1.5px solid rgba(220,38,38,0.40)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                        <WarningIcon sx={{ fontSize: "1.2rem", color: "#dc2626" }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: tokens.textPrimary, lineHeight: 1.3 }}>
                            Unable to submit Rx request
                        </Typography>
                        <Typography sx={{ fontSize: "0.72rem", color: tokens.textSecondary, mt: 0.1 }}>
                            State license requirements were not met
                        </Typography>
                    </Box>
                    <IconButton
                        onClick={() => setShowGatingBlockPopup(false)}
                        size="small"
                        sx={{
                            width: 28, height: 28, borderRadius: "7px",
                            color: tokens.textSecondary,
                            "&:hover": { backgroundColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)", color: tokens.textPrimary },
                        }}
                    >
                        <CloseIcon sx={{ fontSize: "0.9rem" }} />
                    </IconButton>
                </Box>

                <Box sx={{ px: 2.5, py: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
                    {gatingFailures.map((failure) => (
                        <Box
                            key={`${failure.gate}-${failure.code}`}
                            sx={{
                                borderRadius: "10px",
                                border: "1px solid rgba(220,38,38,0.30)",
                                borderLeft: "4px solid #dc2626",
                                backgroundColor: isDark ? "rgba(220,38,38,0.08)" : "rgba(220,38,38,0.05)",
                                px: 2,
                                py: 1.5,
                            }}
                        >
                            <Typography sx={{
                                fontSize: "0.7rem",
                                fontWeight: 700,
                                letterSpacing: "0.04em",
                                textTransform: "uppercase",
                                color: "#dc2626",
                                mb: 0.5,
                            }}>
                                {failure.gate === "pharmacy"
                                    ? "Pharmacy license (ship-to state)"
                                    : "Product restriction"}
                            </Typography>
                            <Typography sx={{ fontSize: "0.86rem", color: tokens.textPrimary, lineHeight: 1.55 }}>
                                {failure.message}
                            </Typography>
                        </Box>
                    ))}
                </Box>

                <Box sx={{
                    px: 2.5, py: 1.5,
                    borderTop: `1px solid ${tokens.border}`,
                    display: "flex",
                    justifyContent: "flex-end",
                }}>
                    <Button
                        variant="contained"
                        onClick={() => setShowGatingBlockPopup(false)}
                        sx={{
                            ...primaryBtnSx,
                            backgroundColor: "#dc2626",
                            "&:hover": { backgroundColor: "#b91c1c" },
                            borderRadius: "8px",
                        }}
                    >
                        OK
                    </Button>
                </Box>
            </Dialog>

            <Dialog
                open={showPrescriberConfirmPopup}
                onClose={() => {
                    setShowPrescriberConfirmPopup(false);
                    setPrescriberConfirmChecked(false);
                }}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        ...paperSx(false),
                        p: 0,
                        borderRadius: "14px",
                        width: "620px",
                        maxWidth: "620px",
                    },
                }}
            >
                <Box sx={{
                    display: "flex", alignItems: "center", gap: 1.5,
                    px: 2.5, py: 1.5,
                    borderBottom: `1px solid ${tokens.border}`,
                }}>
                    <Box sx={{
                        width: 40, height: 40, borderRadius: "10px", flexShrink: 0,
                        backgroundColor: isDark ? "rgba(20,184,166,0.15)" : "rgba(20,184,166,0.12)",
                        border: "1.5px solid rgba(20,184,166,0.35)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                        <GppGoodOutlinedIcon sx={{ fontSize: "1.2rem", color: tokens.accent }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: tokens.textPrimary, lineHeight: 1.3 }}>
                            Prescriber Confirmation
                        </Typography>
                        <Typography sx={{ fontSize: "0.72rem", color: tokens.textSecondary, mt: 0.1 }}>
                            Required before submitting this Rx request
                        </Typography>
                    </Box>
                    <IconButton
                        onClick={() => {
                            setShowPrescriberConfirmPopup(false);
                            setPrescriberConfirmChecked(false);
                        }}
                        size="small"
                        sx={{
                            width: 28, height: 28, borderRadius: "7px",
                            color: tokens.textSecondary,
                            "&:hover": { backgroundColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)", color: tokens.textPrimary },
                        }}
                    >
                        <CloseIcon sx={{ fontSize: "0.9rem" }} />
                    </IconButton>
                </Box>

                <Box sx={{ px: 2.5, py: 2, display: "flex", flexDirection: "column", gap: 2 }}>
                    <Box sx={{
                        borderRadius: "10px",
                        border: `1px solid ${tokens.border}`,
                        borderLeft: `4px solid ${tokens.accent}`,
                        backgroundColor: isDark ? "rgba(20,184,166,0.08)" : "rgba(20,184,166,0.06)",
                        px: 2,
                        py: 1.8,
                    }}>
                        <Typography sx={{ fontSize: "0.88rem", color: tokens.textPrimary, lineHeight: 1.7 }}>
                            {prescriberConfirmPopupCopy.acknowledgmentText}
                        </Typography>
                    </Box>

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={prescriberConfirmChecked}
                                onChange={(e) => setPrescriberConfirmChecked(e.target.checked)}
                                sx={{
                                    color: tokens.textSecondary,
                                    "&.Mui-checked": { color: tokens.accent },
                                }}
                            />
                        }
                        label={
                            <Typography sx={{ fontSize: "0.86rem", color: tokens.textPrimary, fontWeight: 600 }}>
                                I confirm the above.
                                <Box component="span" sx={{ color: tokens.accent, ml: 0.4 }}>*</Box>
                            </Typography>
                        }
                        sx={{ alignItems: "center", ml: 0, mr: 0 }}
                    />
                </Box>

                <Box sx={{
                    px: 2.5, py: 1.5,
                    borderTop: `1px solid ${tokens.border}`,
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 1.5,
                }}>
                    <Button
                        variant="outlined"
                        onClick={() => {
                            setShowPrescriberConfirmPopup(false);
                            setPrescriberConfirmChecked(false);
                        }}
                        sx={cancelBtnSx}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        disabled={!prescriberConfirmChecked || isLoggingPrescriberConfirmation}
                        onClick={handlePrescriberConfirmContinue}
                        sx={{
                            ...primaryBtnSx,
                            "&.Mui-disabled": {
                                backgroundColor: isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0",
                                color: isDark ? "rgba(255,255,255,0.3)" : "#94a3b8",
                            },
                        }}
                    >
                        Continue
                    </Button>
                </Box>
            </Dialog>

            <Dialog
    open={showPharmacyBillingSetupPopup}
    onClose={() => setShowPharmacyBillingSetupPopup(false)}
    maxWidth="sm"
    fullWidth
    PaperProps={{
        sx: {
            ...paperSx(false),
            p: 0,
            borderRadius: "14px",
            width: "620px",
            maxWidth: "620px",
        }
    }}
>
    {/* ── Header ── */}
    <Box sx={{
        display: "flex", alignItems: "center", gap: 1.5,
        px: 2.5, py: 1.5,
        borderBottom: `1px solid ${tokens.border}`,
    }}>
        {/* Large amber rounded-square icon box — this matches the image exactly */}
        <Box sx={{
            width: 40, height: 40, borderRadius: "10px", flexShrink: 0,
            backgroundColor: "rgba(245,158,11,0.18)",
            border: "1.5px solid rgba(245,158,11,0.45)",
            display: "flex", alignItems: "center", justifyContent: "center",
        }}>
            <WarningIcon sx={{ fontSize: "1.2rem", color: "#f59e0b" }} />
        </Box>
        <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: tokens.textPrimary, lineHeight: 1.3 }}>
                {`Finalize your account with ${pharmacyDisplayName}`}
            </Typography>
            <Typography sx={{ fontSize: "0.72rem", color: tokens.textSecondary, mt: 0.1 }}>
                Billing setup required
            </Typography>
        </Box>
        <IconButton
            onClick={() => setShowPharmacyBillingSetupPopup(false)}
            size="small"
            sx={{
                width: 28, height: 28, borderRadius: "7px",
                color: tokens.textSecondary,
                "&:hover": { backgroundColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)", color: tokens.textPrimary },
            }}
        >
            <CloseIcon sx={{ fontSize: "0.9rem" }} />
        </IconButton>
    </Box>

    {/* ── Body ── */}
    <Box sx={{ px: 2.5, py: 2, display: "flex", flexDirection: "column", gap: 2 }}>

        {/* Amber info card with left border */}
        <Box sx={{
            borderRadius: "10px",
            border: "1px solid rgba(245,158,11,0.35)",
            borderLeft: "4px solid #f59e0b",
            backgroundColor: isDark ? "rgba(245,158,11,0.08)" : "rgba(245,158,11,0.07)",
            px: 2, py: 1.8,
            display: "flex", alignItems: "flex-start", gap: 1.5,
        }}>
            <Box sx={{
                width: 30, height: 30, borderRadius: "50%", flexShrink: 0, mt: 0.1,
                border: "1.5px solid #f59e0b",
                backgroundColor: "transparent",
                display: "flex", alignItems: "center", justifyContent: "center",
            }}>
                <Box component="span" sx={{ fontSize: "0.95rem", color: "#f59e0b", fontWeight: 900, lineHeight: 1 }}>!</Box>
            </Box>
            <Typography sx={{ fontSize: "0.88rem", color: tokens.textPrimary, lineHeight: 1.7 }}>
                {`Since this is your first order through this integration, your billing account with ${pharmacyDisplayName} needs to be set up before your prescription request can be submitted.`}
                <br />
                Please follow the steps below:
            </Typography>
        </Box>

        {/* Steps list */}
        <Box sx={{
            border: `1px solid ${tokens.border}`,
            borderRadius: "10px",
            overflow: "hidden",
            backgroundColor: isDark ? "rgba(255,255,255,0.02)" : tokens.inputBg,
        }}>

            {/* Step 1 */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2, py: 1.3 }}>
                <Box sx={{
                    width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
                    border: "1.5px solid #f59e0b",
                    backgroundColor: "transparent",
                    display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                    <Typography sx={{ fontSize: "0.72rem", fontWeight: 800, color: "#f59e0b" }}>1</Typography>
                </Box>
                <Box sx={{
                    width: 38, height: 38, borderRadius: "8px", flexShrink: 0,
                    backgroundColor: "transparent",
                    border: `1px solid ${tokens.border}`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                    {isGenesisPharmacy
                        ? <EditNoteOutlinedIcon sx={{ fontSize: "1.1rem", color: "#f59e0b" }} />
                        : <DownloadIcon sx={{ fontSize: "1.1rem", color: "#f59e0b" }} />}
                </Box>
                <Typography sx={{ fontSize: "0.83rem", color: tokens.textPrimary, flex: 1, lineHeight: 1.4 }}>
                    {isGenesisPharmacy
                        ? "Fill up the Credit Card Authorization form."
                        : "Download the Account setup form."}
                </Typography>
                <Button
                    variant="outlined"
                    size="small"
                    endIcon={isGenesisPharmacy
                        ? <EditNoteOutlinedIcon sx={{ fontSize: "0.9rem !important" }} />
                        : <DownloadIcon sx={{ fontSize: "0.9rem !important" }} />}
                    onClick={isGenesisPharmacy
                        ? () => setShowGenesisBillingFormPopup(true)
                        : handlePharmacyFormDownload}
                    sx={{
                        flexShrink: 0,
                        textTransform: "none",
                        fontWeight: 700,
                        fontSize: "0.78rem",
                        borderRadius: "7px",
                        color: "#f59e0b",
                        borderColor: "rgba(245,158,11,0.6)",
                        borderWidth: "1.5px",
                        backgroundColor: "transparent",
                        px: 1.4, py: 0.4,
                        "&:hover": {
                            backgroundColor: "rgba(245,158,11,0.08)",
                            borderColor: "#f59e0b",
                            borderWidth: "1.5px",
                        },
                    }}
                >
                    {isGenesisPharmacy ? "Fill Up" : "Download"}
                </Button>
            </Box>

            <Divider sx={{ borderColor: tokens.border, mx: 2 }} />

            {/* Step 2 */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2, py: 1.3 }}>
                <Box sx={{
                    width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
                    border: "1.5px solid #f59e0b",
                    backgroundColor: "transparent",
                    display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                    <Typography sx={{ fontSize: "0.72rem", fontWeight: 800, color: "#f59e0b" }}>2</Typography>
                </Box>
                <Box sx={{
                    width: 38, height: 38, borderRadius: "8px", flexShrink: 0,
                    backgroundColor: "transparent",
                    border: `1px solid ${tokens.border}`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                    <DescriptionOutlinedIcon sx={{ fontSize: "1.1rem", color: "#f59e0b" }} />
                </Box>
                <Typography sx={{ fontSize: "0.83rem", color: tokens.textPrimary, flex: 1, lineHeight: 1.4 }}>
                    Fill in and save all the required details in the form.
                </Typography>
            </Box>

            <Divider sx={{ borderColor: tokens.border, mx: 2 }} />

            {/* Step 3 */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2, py: 1.3 }}>
                <Box sx={{
                    width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
                    border: "1.5px solid #f59e0b",
                    backgroundColor: "transparent",
                    display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                    <Typography sx={{ fontSize: "0.72rem", fontWeight: 800, color: "#f59e0b" }}>3</Typography>
                </Box>
                <Box sx={{
                    width: 38, height: 38, borderRadius: "8px", flexShrink: 0,
                    backgroundColor: "transparent",
                    border: `1px solid ${tokens.border}`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                    <VisibilityOutlinedIcon sx={{ fontSize: "1.1rem", color: "#f59e0b" }} />
                </Box>
                <Typography sx={{ fontSize: "0.83rem", color: tokens.textPrimary, flex: 1, lineHeight: 1.4 }}>
                    Review the information for accuracy.
                </Typography>
            </Box>

            <Divider sx={{ borderColor: tokens.border, mx: 2 }} />

            {/* Step 4 */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2, py: 1.3 }}>
                <Box sx={{
                    width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
                    border: "1.5px solid #f59e0b",
                    backgroundColor: "transparent",
                    display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                    <Typography sx={{ fontSize: "0.72rem", fontWeight: 800, color: "#f59e0b" }}>4</Typography>
                </Box>
                <Box sx={{
                    width: 38, height: 38, borderRadius: "8px", flexShrink: 0,
                    backgroundColor: "transparent",
                    border: `1px solid ${tokens.border}`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M22 2L11 13" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </Box>
                <Typography sx={{ fontSize: "0.83rem", color: tokens.textPrimary, flex: 1, lineHeight: 1.4 }}>
                    Submit the completed form to{" "}
                    <Box
                        component="a"
                        href="mailto:service@v3biomedical.com"
                        sx={{ color: "#f59e0b", fontWeight: 600, textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
                    >
                        service@v3biomedical.com
                    </Box>.
                </Typography>
            </Box>
        </Box>
    </Box>

    {/* ── Footer ── */}
    <Box sx={{
        px: 2.5, py: 2,
        borderTop: `1px solid ${tokens.border}`,
        display: "flex",
        justifyContent: "flex-end",
    }}>
        <Button
            variant="contained"
            onClick={() => setShowPharmacyBillingSetupPopup(false)}
            sx={{
                ...primaryBtnSx,
                px: 5,
                py: 0.6,
                fontSize: "0.82rem",
                fontWeight: 700,
                borderRadius: "8px",
            }}
        >
            OK
        </Button>
    </Box>
</Dialog>

            <Dialog
                open={showBillingInfoPopup}
                onClose={() => setShowBillingInfoPopup(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        ...paperSx(false),
                        p: 0,
                        borderRadius: "14px",
                        width: "620px",
                        maxWidth: "620px",
                    }
                }}
            >
                {/* ── Header ── */}
                <Box sx={{
                    display: "flex", alignItems: "center", gap: 1.5,
                    px: 2.5, py: 1.5,
                    borderBottom: `1px solid ${tokens.border}`,
                }}>
                    <Box sx={{
                        width: 40, height: 40, borderRadius: "10px", flexShrink: 0,
                        backgroundColor: isDark ? "rgba(20,184,166,0.15)" : "rgba(20,184,166,0.12)",
                        border: `1.5px solid rgba(20,184,166,0.35)`,
                        display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                        <InfoOutlinedIcon sx={{ fontSize: "1.2rem", color: tokens.accent }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: tokens.textPrimary, lineHeight: 1.3 }}>
                            Billing Information
                        </Typography>
                        <Typography sx={{ fontSize: "0.72rem", color: tokens.textSecondary, mt: 0.1 }}>
                            {`${pharmacyDisplayName} billing account setup`}
                        </Typography>
                    </Box>
                    <IconButton
                        onClick={() => setShowBillingInfoPopup(false)}
                        size="small"
                        sx={{
                            width: 28, height: 28, borderRadius: "7px",
                            color: tokens.textSecondary,
                            "&:hover": { backgroundColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)", color: tokens.textPrimary },
                        }}
                    >
                        <CloseIcon sx={{ fontSize: "0.9rem" }} />
                    </IconButton>
                </Box>

                {/* ── Body ── */}
                <Box sx={{ px: 2.5, py: 2, display: "flex", flexDirection: "column", gap: 2 }}>

                    {/* Info card */}
                    <Box sx={{
                        border: `1px solid ${tokens.border}`, borderRadius: "10px",
                        p: 1.8, display: "flex", alignItems: "flex-start", gap: 1.5,
                        backgroundColor: isDark ? "rgba(20,184,166,0.12)" : "rgba(20,184,166,0.10)",
                    }}>
                        <Box sx={{
                            width: 36, height: 36, borderRadius: "8px", flexShrink: 0,
                            display: "flex", alignItems: "center", justifyContent: "center",
                        }}>
                            <ReceiptOutlined sx={{ fontSize: "2.1rem", color: tokens.accent }} />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography sx={{ fontSize: "0.82rem", color: tokens.textSecondary, lineHeight: 1.65, mb: 0.8 }}>
                                If your preference is for{" "}
                                <Box component="span" sx={{ fontWeight: 700, color: tokens.textPrimary }}>{`${pharmacyDisplayName} to charge the patient directly`}</Box>
                                {`, there’s no need to set up a billing account. ${pharmacyDisplayName} will contact the patient directly for billing.`}
                            </Typography>
                            <Typography sx={{ fontSize: "0.82rem", color: tokens.textSecondary, lineHeight: 1.65 }}>
                                If you prefer to{" "}
                                <Box component="span" sx={{ fontWeight: 700, color: tokens.textPrimary }}>bill the practice</Box>
                                {`, your billing account with ${pharmacyDisplayName} must be set up before your first prescription request can be submitted through this integration.`}
                            </Typography>
                        </Box>
                    </Box>

                    {/* Steps */}
                    <Box>
                        <Typography sx={{ fontSize: "0.88rem", fontWeight: 700, color: tokens.textPrimary, mb: 1.2 }}>
                            Please complete the following steps:
                        </Typography>
                        <Box sx={{
                            border: `1px solid ${tokens.border}`, borderRadius: "10px",
                            overflow: "hidden",
                            backgroundColor: isDark ? "rgba(255,255,255,0.02)" : tokens.inputBg,
                        }}>
                            {/* Step 1 */}
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2, py: 1.3 }}>
                                <Box sx={{
                                    width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
                                    backgroundColor: tokens.accent,
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                }}>
                                    <Typography sx={{ fontSize: "0.72rem", fontWeight: 800, color: "#fff" }}>1</Typography>
                                </Box>
                                <Box sx={{
                                    width: 38, height: 38, borderRadius: "8px", flexShrink: 0,
                                    backgroundColor: "transparent",
                                    border: `1px solid ${tokens.border}`,
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                }}>
                                    {isGenesisPharmacy
                                        ? <EditNoteOutlinedIcon sx={{ fontSize: "1.1rem", color: tokens.accent }} />
                                        : <DownloadIcon sx={{ fontSize: "1.1rem", color: tokens.accent }} />}
                                </Box>
                                <Typography sx={{ fontSize: "0.83rem", color: tokens.textPrimary, flex: 1, lineHeight: 1.4 }}>
                                    {isGenesisPharmacy
                                        ? "Fill Up the Credit Card Authorization Form."
                                        : "Download the Account Setup Form."}
                                </Typography>
                                <Button
                                    variant="outlined" size="small"
                                    endIcon={isGenesisPharmacy
                                        ? <EditNoteOutlinedIcon sx={{ fontSize: "0.85rem !important" }} />
                                        : <DownloadIcon sx={{ fontSize: "0.85rem !important" }} />}
                                    onClick={isGenesisPharmacy
                                        ? () => setShowGenesisBillingFormPopup(true)
                                        : handlePharmacyFormDownload}
                                    sx={{
                                        flexShrink: 0, textTransform: "none", fontWeight: 700,
                                        fontSize: "0.78rem", borderRadius: "7px",
                                        color: tokens.accent, borderColor: tokens.accent, borderWidth: "1.5px",
                                        px: 1.4, py: 0.4,
                                        "&:hover": { backgroundColor: "rgba(20,184,166,0.08)", borderColor: tokens.accent, borderWidth: "1.5px" },
                                    }}
                                >
                                    {isGenesisPharmacy ? "Fill Up" : "Download"}
                                </Button>
                            </Box>

                            <Divider sx={{ borderColor: tokens.border, mx: 2 }} />

                            {/* Step 2 */}
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2, py: 1.3 }}>
                                <Box sx={{ width: 28, height: 28, borderRadius: "50%", flexShrink: 0, backgroundColor: tokens.accent, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    <Typography sx={{ fontSize: "0.72rem", fontWeight: 800, color: "#fff" }}>2</Typography>
                                </Box>
                                <Box sx={{ width: 38, height: 38, borderRadius: "8px", flexShrink: 0, backgroundColor: "transparent", border: `1px solid ${tokens.border}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    <DescriptionOutlinedIcon sx={{ fontSize: "1.1rem", color: tokens.accent }} />
                                </Box>
                                <Typography sx={{ fontSize: "0.83rem", color: tokens.textPrimary, flex: 1, lineHeight: 1.4 }}>
                                    Fill out all required details and save the completed form.
                                </Typography>
                            </Box>

                            <Divider sx={{ borderColor: tokens.border, mx: 2 }} />

                            {/* Step 3 */}
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2, py: 1.3 }}>
                                <Box sx={{ width: 28, height: 28, borderRadius: "50%", flexShrink: 0, backgroundColor: tokens.accent, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    <Typography sx={{ fontSize: "0.72rem", fontWeight: 800, color: "#fff" }}>3</Typography>
                                </Box>
                                <Box sx={{ width: 38, height: 38, borderRadius: "8px", flexShrink: 0, backgroundColor: "transparent", border: `1px solid ${tokens.border}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    <VisibilityOutlinedIcon sx={{ fontSize: "1.1rem", color: tokens.accent }} />
                                </Box>
                                <Typography sx={{ fontSize: "0.83rem", color: tokens.textPrimary, flex: 1, lineHeight: 1.4 }}>
                                    Review the information for accuracy.
                                </Typography>
                            </Box>

                            <Divider sx={{ borderColor: tokens.border, mx: 2 }} />

                            {/* Step 4 */}
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2, py: 1.3 }}>
                                <Box sx={{ width: 28, height: 28, borderRadius: "50%", flexShrink: 0, backgroundColor: tokens.accent, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    <Typography sx={{ fontSize: "0.72rem", fontWeight: 800, color: "#fff" }}>4</Typography>
                                </Box>
                                <Box sx={{ width: 38, height: 38, borderRadius: "8px", flexShrink: 0, backgroundColor: "transparent", border: `1px solid ${tokens.border}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M22 2L11 13" stroke="#14b8a6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="#14b8a6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                </Box>
                                <Typography sx={{ fontSize: "0.83rem", color: tokens.textPrimary, flex: 1, lineHeight: 1.4 }}>
                                    Submit the completed form to{" "}
                                    <Box component="a" href="mailto:service@v3biomedical.com" sx={{ color: tokens.accent, fontWeight: 600, textDecoration: "none", "&:hover": { textDecoration: "underline" } }}>
                                        service@v3biomedical.com
                                    </Box>.
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    {/* Footer note */}
                    <Box sx={{
                        border: `1px solid ${tokens.border}`, borderRadius: "10px",
                        px: 2, py: 1.5, display: "flex", alignItems: "flex-start", gap: 1.5,
                        backgroundColor: isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.018)",
                    }}>
                        <Box sx={{ width: 24, height: 24, borderRadius: "50%", flexShrink: 0, mt: 0.1, border: `1.5px solid ${tokens.border}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <InfoOutlinedIcon sx={{ fontSize: "0.85rem", color: tokens.textSecondary }} />
                        </Box>
                        <Typography sx={{ fontSize: "0.78rem", color: tokens.textSecondary, lineHeight: 1.6 }}>
                            {`Once your billing account is set up with ${pharmacyDisplayName}, you will be able to submit prescription requests using the practice billing option.`}
                        </Typography>
                    </Box>
                </Box>

                {/* ── Footer ── */}
                <Box sx={{
                    ...footerSx,
                    borderTop: `1px solid ${tokens.border}`,
                    display: "flex",
                    justifyContent: "right",
                    px: 2.5, py: 1.5,
                }}>
                    <Button
                        variant="contained"
                        onClick={() => setShowBillingInfoPopup(false)}
                        sx={{ ...primaryBtnSx, px: 5, py: 0.6, fontSize: "0.82rem", borderRadius: "8px" }}
                    >
                        OK
                    </Button>
                </Box>
            </Dialog>

            {/* CS-2075 — Genesis account setup form, filled in-app instead of the PDF download */}
            <GenesisBillingFormPopup
                open={showGenesisBillingFormPopup}
                onClose={() => setShowGenesisBillingFormPopup(false)}
                pharmacyDisplayName={pharmacyDisplayName}
                prefill={genesisBillingPrefill}
            />
        </>
    );
};

export default PrescriptionModalPopup;